import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  Loader2, LogOut, Sparkles, Calendar, TrendingUp,
  RefreshCw, Users, Ban, Unlock,
  MessageSquare, Bot, KeyRound, Plus, Send, IdCard, Activity, Home,
  Wallet, Edit3, CheckCircle2, XCircle, Trash2, CalendarClock, MessageCircleMore,
  Receipt, AlertCircle, UserMinus, ClipboardEdit, FileCheck, FileX, Eye,
  Sparkle, Clock, RotateCcw, FileText, X, Settings, Sun, Moon, Camera, Megaphone, BarChart3, Gift, Mic, MicOff, Brain, MapPin, EyeOff, Star,
  History, Database, Cog, Fingerprint, Link2, UserPlus, PhoneCall, MessageCircle, Phone, PenTool, MessageSquareQuote, Bell, Image as ImageIcon, ArrowRight, Percent, ScrollText,
  UserCog, ChevronUp, ChevronDown, BookOpenCheck,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useRealtimeEvents } from "@/hooks/useRealtimeEvents";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  GrantReadingModal, ProfileReviewModal, ReadingDetailModal, ReceiptPreviewModal,
} from "@/components/admin/AdminModals";
import { SendReceiptModal } from "@/components/admin/SendReceiptModal";
import { LivePulse } from "@/components/admin/LivePulse";
import { AdminChatDock } from "@/components/admin/AdminChatDock";
import { AdminSettingsTab, AdminBroadcastTab } from "@/components/admin/AdminSettingsTab";
import { AdminUserCommentsTab } from "@/components/admin/AdminUserCommentsTab";
import { AdminReportsTab } from "@/components/admin/AdminReportsTab";
import { AdminSorteoTab } from "@/components/admin/AdminSorteoTab";
import { AdminVidenteRatingsTab } from "@/components/admin/AdminVidenteRatingsTab";
import { AdminVidenteMonitorTab } from "@/components/admin/AdminVidenteMonitorTab";
import { UserDossierModal } from "@/components/admin/UserDossierModal";
import { CreateUserModal } from "@/components/admin/CreateUserModal";
import { AdminPaymentRequestsSection } from "@/components/AdminPaymentRequestsSection";
import { AdminOverviewSection } from "@/components/AdminOverviewSection";
import { AdminDatabaseSection } from "@/components/admin/AdminDatabaseSection";
import { AdminConfigSection } from "@/components/admin/AdminConfigSection";
import { AdminReadingFormsSection } from "@/components/admin/AdminReadingFormsSection";
import { UserSessionsModal } from "@/components/admin/UserSessionsModal";
import { AdminNetworkInfoModal } from "@/components/admin/AdminNetworkInfoModal";
import { AdminAccessStatsPanel } from "@/components/admin/AdminAccessStatsPanel";
import { PlanBookingLinkModal } from "@/components/admin/PlanBookingLinkModal";
import { PlanSignatureLinkModal } from "@/components/admin/PlanSignatureLinkModal";
import { PlanPetitionLinkModal } from "@/components/admin/PlanPetitionLinkModal";
import { AdminAssistant } from "@/components/admin/AdminAssistant";
import { TempPromoModal } from "@/components/admin/TempPromoModal";
import { PromoExtendModal } from "@/components/admin/PromoExtendModal";
import { getExtendablePromo } from "@/utils/promoState";
import { PromotionsSection } from "@/components/admin/PromotionsSection";
import { ReceiptShareModal } from "@/components/admin/ReceiptShareModal";
import { InstallmentModal } from "@/components/admin/InstallmentModal";
import { ReflectedShareModal } from "@/components/admin/ReflectedShareModal";
import { PromoLiquidateDialog } from "@/components/admin/PromoLiquidateDialog";
import { OpportunitiesModal } from "@/components/admin/OpportunitiesModal";
import { EditUserModal } from "@/components/admin/EditUserModal";
import { WhatsAppPanel } from "@/components/admin/WhatsAppPanel";
import { AdminGlobalSearch } from "@/components/admin/AdminGlobalSearch";
import { PushPermissionBanner } from "@/components/PushPermissionBanner";
import { Avatar } from "@/components/Avatar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  { id: "lectura_basica", name: "Lectura Básica" },
  { id: "lectura_completa", name: "Lectura Completa con Runas" },
  { id: "lectura_completa_video", name: "Lectura con Runas (Videollamada)" },
  { id: "magia_blanca", name: "Magia Blanca" },
  { id: "magia_negra", name: "Magia Negra + Protección" },
  { id: "magia_suprema", name: "Magia Suprema" },
  { id: "poder_maximo", name: "Poder Máximo" },
  { id: "pacto_luz", name: "Pacto de Luz (Seguro)" },
  { id: "pacto_satanico", name: "Pacto Satánico (Seguro)" },
];

const STATUS_STYLES = {
  scheduled: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  rescheduled: "border-amber-500/40 bg-amber-500/10 text-amber-300",
  attended: "border-sky-500/40 bg-sky-500/10 text-sky-300",
  cancelled: "border-red-500/40 bg-red-500/10 text-red-300",
};

const STATUS_LABEL = {
  scheduled: "Agendada",
  rescheduled: "Reagendada",
  attended: "Atendida",
  cancelled: "Cancelada",
};

const PLAN_STATUS = {
  active: "border-amber-500/40 bg-amber-500/10 text-amber-300",
  paid: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  cancelled: "border-red-500/40 bg-red-500/10 text-red-300",
};

const fmt = (n, c = "MXN") => `$${Number(n || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 })} ${c}`;

export default function AdminDashboard() {
  const { user, logout, authHeader } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [chats, setChats] = useState([]);
  const [appts, setAppts] = useState([]);
  const [plans, setPlans] = useState([]);
  const [activity, setActivity] = useState([]);
  const [profileReqs, setProfileReqs] = useState([]);
  const [readings, setReadings] = useState([]);
  const [tab, setTab] = useState("users");
  const [loading, setLoading] = useState(true);
  const [openChat, setOpenChat] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPw, setResetPw] = useState("");
  // Per-user revealed plaintext password { [user_id]: "actual_password" }
  const [revealedPw, setRevealedPw] = useState({});
  // Per-user revealed GPS coordinates { [user_id]: { lat, lng, accuracy } }
  const [revealedGeo, setRevealedGeo] = useState({});
  // User whose session history (GPS + silent photos) is being viewed
  const [sessionsUser, setSessionsUser] = useState(null);
  const [networkUser, setNetworkUser] = useState(null);

  // appointment form + edit modal
  const [apt, setApt] = useState({ user_visible_id: "", service_id: "", scheduled_date: "", scheduled_time: "", notes: "" });
  const [editApt, setEditApt] = useState(null); // current appointment being edited
  const [aptEdit, setAptEdit] = useState({});
  const [aptComment, setAptComment] = useState("");

  // payment plans form + modals
  const EMPTY_PLAN_FORM = { user_visible_id: "", service_id: "", custom_name: "", custom_category: "hechiceria", register_custom: true, allow_signature: null, agreed_price: "", currency: "MXN", payment_promise_date: "", notes: "", installments_count: 1, frequency: "single", custom_interval_days: "" };
  const [planForm, setPlanForm] = useState(EMPTY_PLAN_FORM);
  const [planServices, setPlanServices] = useState([]);
  const [planUserActionsOpen, setPlanUserActionsOpen] = useState({});
  const loadPlanServices = useCallback(async () => {
    try { const r = await axios.get(`${API}/admin/plan-services`, { headers: authHeader() }); setPlanServices(r.data || []); } catch { /* keep static list */ }
    // eslint-disable-next-line
  }, []);
  useEffect(() => { loadPlanServices(); }, [loadPlanServices]);
  const [planMode, setPlanMode] = useState("service"); // "service" | "custom"
  const [editPlan, setEditPlan] = useState(null);
  const [planEdit, setPlanEdit] = useState({});
  const [planComment, setPlanComment] = useState("");
  const [usersFilter, setUsersFilter] = useState("");
  const [usersStatusFilter, setUsersStatusFilter] = useState("all"); // all | active | blocked | admin
  const [apptFilter, setApptFilter] = useState("");
  const [plansFilter, setPlansFilter] = useState("");
  const [dossierUserId, setDossierUserId] = useState(null);
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [installPlan, setInstallPlan] = useState(null); // plan we are adding installment to
  const [installForm, setInstallForm] = useState({ amount: "", method: "", notes: "", receipt_file: null, reflected: true });
  const [installBusy, setInstallBusy] = useState(false);
  const [editInstall, setEditInstall] = useState(null); // installment being edited
  const [editInstallForm, setEditInstallForm] = useState({ amount: "", method: "", notes: "" });
  const [editInstallBusy, setEditInstallBusy] = useState(false);

  // booking link modal — opens after admin registers a plan or installment
  const [bookingLinkPlan, setBookingLinkPlan] = useState(null);
  const [bookingLinkVariant, setBookingLinkVariant] = useState("created"); // "created" | "installment"

  // signature link modal — opens when admin sends the signature+INE link
  const [signatureLinkPlan, setSignatureLinkPlan] = useState(null);
  const [petitionPlan, setPetitionPlan] = useState(null);

  // receipts gallery modal — thumbnails grid + zoom for a plan's comprobantes
  const [galleryPlan, setGalleryPlan] = useState(null);        // plan being browsed
  const [galleryItems, setGalleryItems] = useState([]);        // metadata list
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryZoom, setGalleryZoom] = useState(null);        // { data_b64, mime_type, filename, receipt_id }
  const [galleryZoomLoading, setGalleryZoomLoading] = useState(false);
  const [galleryStatusBusy, setGalleryStatusBusy] = useState(false);
  const [galleryFilterInst, setGalleryFilterInst] = useState("all"); // "all" | installment_id | "unlinked"

  // Cache thumbnail data URLs by receipt id (loaded on demand, one per row).
  const [galleryThumbs, setGalleryThumbs] = useState({});

  // Testimonial-link modal (post-attendance)
  const [testimonialApt, setTestimonialApt] = useState(null);   // appointment being viewed
  const [testimonialInfo, setTestimonialInfo] = useState(null); // {has_link, url, used, comment?}
  const [testimonialBusy, setTestimonialBusy] = useState(false);
  // Cache of testimonials indexed by user_id (used in Pagos tab for the "🌟 Testimonio" preview)
  const [userTestimonialsMap, setUserTestimonialsMap] = useState({});
  // Offer config modal (Users tab) + Add-offer modal (Pagos tab)
  const [offerConfigUser, setOfferConfigUser] = useState(null);
  const [offerConfigForm, setOfferConfigForm] = useState({ allow_offers: false, max_offer_pct: "", max_offer_amount: "" });
  const [offerConfigBusy, setOfferConfigBusy] = useState(false);
  const [offerApplyPlan, setOfferApplyPlan] = useState(null);
  const [offerApplyForm, setOfferApplyForm] = useState({ kind: "amount", value: "", note: "" });
  const [offerApplyBusy, setOfferApplyBusy] = useState(false);
  // Temporary discount promotion modal (per plan)
  const [promoPlan, setPromoPlan] = useState(null);
  const [promoExtendTarget, setPromoExtendTarget] = useState(null); // {plan, promo}
  const [promoLiquidateTarget, setPromoLiquidateTarget] = useState(null); // {plan, promo}
  const [reflectedShare, setReflectedShare] = useState(null);
  const [opportunitiesPlan, setOpportunitiesPlan] = useState(null);
  const [editUser, setEditUser] = useState(null);
  // WhatsApp share panel after registering an installment (auto-receipt)
  const [receiptShare, setReceiptShare] = useState(null);
  // Legal PDFs dropdown state (per signature_id)
  const [legalDocsOpen, setLegalDocsOpen] = useState(null); // { plan_id, sid, items }
  const [legalDocsLoading, setLegalDocsLoading] = useState(false);

  // Load offer config when opening the Users offer modal
  useEffect(() => {
    if (!offerConfigUser) return;
    setOfferConfigForm({
      allow_offers: !!offerConfigUser.allow_offers,
      max_offer_pct: offerConfigUser.max_offer_pct ?? "",
      max_offer_amount: offerConfigUser.max_offer_amount ?? "",
    });
  }, [offerConfigUser]);

  const saveOfferConfig = async () => {
    if (!offerConfigUser || offerConfigBusy) return;
    setOfferConfigBusy(true);
    try {
      const payload = {
        allow_offers: !!offerConfigForm.allow_offers,
        max_offer_pct: offerConfigForm.max_offer_pct === "" ? null : Number(offerConfigForm.max_offer_pct),
        max_offer_amount: offerConfigForm.max_offer_amount === "" ? null : Number(offerConfigForm.max_offer_amount),
      };
      await axios.patch(`${API}/admin/users/${offerConfigUser.id}/offers`, payload, { headers: authHeader() });
      toast.success(payload.allow_offers ? "Ofertas habilitadas para este usuario" : "Ofertas deshabilitadas");
      setOfferConfigUser(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setOfferConfigBusy(false);
    }
  };

  const applyOffer = async () => {
    if (!offerApplyPlan || offerApplyBusy) return;
    const v = Number(offerApplyForm.value);
    if (!v || v <= 0) { toast.error("Ingresa un valor válido"); return; }
    setOfferApplyBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/payment-plans/${offerApplyPlan.id}/offers`,
        { kind: offerApplyForm.kind, value: v, note: offerApplyForm.note || null },
        { headers: authHeader() },
      );
      toast.success(`Descuento de $${r.data.discount_amount.toLocaleString("es-MX")} aplicado — nuevo total $${r.data.agreed_price_after_offer.toLocaleString("es-MX")}`);
      setOfferApplyPlan(null);
      setOfferApplyForm({ kind: "amount", value: "", note: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setOfferApplyBusy(false);
    }
  };

  const removeOffer = async (plan, offerId) => {
    if (!window.confirm("¿Eliminar esta oferta? El precio original se restablecerá.")) return;
    try {
      await axios.delete(`${API}/admin/payment-plans/${plan.id}/offers/${offerId}`, { headers: authHeader() });
      toast.success("Oferta eliminada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  // All per-user action buttons (Usuarios tab). Reused, collapsed, inside each
  // Pagos/Trabajos row so the admin has every client action at hand.
  const renderUserActions = (u) => (
    <>
          <button data-testid={`quick-apt-${u.id}`} onClick={() => quickAppt(u)} title="Crear cita rápida" className="text-xs px-2 py-1 rounded border border-white/10 hover:border-emerald-500/40 text-neutral-300 hover:text-emerald-300 flex items-center gap-1"><Calendar className="w-3 h-3" /></button>
          <button data-testid={`quick-pay-${u.id}`} onClick={() => quickPayment(u)} title="Registrar pago" className="text-xs px-2 py-1 rounded border border-white/10 hover:border-amber-500/40 text-neutral-300 hover:text-amber-300 flex items-center gap-1"><Wallet className="w-3 h-3" /></button>
          <button data-testid={`quick-read-${u.id}`} onClick={() => quickGrant(u)} title="Habilitar lectura" className="text-xs px-2 py-1 rounded border border-white/10 hover:border-purple-500/40 text-neutral-300 hover:text-purple-300 flex items-center gap-1"><Sparkle className="w-3 h-3" /></button>
          <button data-testid={`btn-edit-${u.id}`} onClick={() => setEditUser(u)} title="Editar datos del usuario (nombre, teléfono, fecha de nacimiento…)" className="text-xs px-2 py-1 rounded border border-white/10 hover:border-[#d4af37]/40 text-neutral-300 hover:text-[#d4af37] flex items-center gap-1"><Edit3 className="w-3 h-3" /></button>
          <button data-testid={`btn-block-${u.id}`} onClick={() => toggleBlock(u.id, !u.blocked)} title={u.blocked ? "Desbloquear" : "Bloquear"} className="text-xs px-2 py-1 rounded border border-white/10 hover:border-red-500/40 text-neutral-300 hover:text-red-300 flex items-center gap-1">{u.blocked ? <Unlock className="w-3 h-3" /> : <Ban className="w-3 h-3" />}</button>
          <button data-testid={`btn-msg-${u.id}`} onClick={() => setReplyTo(u)} title="Enviar mensaje" className="text-xs px-2 py-1 rounded border border-white/10 hover:border-[#d4af37]/40 text-neutral-300 hover:text-[#d4af37] flex items-center gap-1"><MessageSquare className="w-3 h-3" /></button>
          <button data-testid={`btn-receipt-${u.id}`} onClick={() => setSendReceiptTo(u)} title="Enviar recibo de pago" className="text-xs px-2 py-1 rounded border border-white/10 hover:border-emerald-500/40 text-neutral-300 hover:text-emerald-300 flex items-center gap-1"><Receipt className="w-3 h-3" /></button>
          <button data-testid={`btn-reset-${u.id}`} onClick={() => setResetTarget(u)} title="Reset contraseña" className="text-xs px-2 py-1 rounded border border-white/10 hover:border-amber-500/40 text-neutral-300 hover:text-amber-300 flex items-center gap-1"><KeyRound className="w-3 h-3" /></button>
          <button data-testid={`btn-geo-${u.id}`} onClick={() => revealLocation(u)} title={revealedGeo[u.id] ? "Ocultar ubicación" : "Ver ubicación GPS"} className={`text-xs px-2 py-1 rounded border flex items-center gap-1 ${revealedGeo[u.id] ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" : "border-white/10 text-neutral-300 hover:border-emerald-500/40 hover:text-emerald-300"}`}><MapPin className="w-3 h-3" /></button>
          <button data-testid={`btn-sessions-${u.id}`} onClick={() => setSessionsUser({ id: u.id, name: u.name || u.username })} title="Ver historial de sesiones (GPS + fotos)" className="text-xs px-2 py-1 rounded border border-white/10 hover:border-cyan-500/40 text-neutral-300 hover:text-cyan-300 flex items-center gap-1"><History className="w-3 h-3" /></button>
          <button data-testid={`btn-network-${u.id}`} onClick={() => setNetworkUser({ id: u.id, name: u.name || u.username })} title="Ver IP y huella del dispositivo" className="text-xs px-2 py-1 rounded border border-white/10 hover:border-indigo-500/40 text-neutral-300 hover:text-indigo-300 flex items-center gap-1"><Fingerprint className="w-3 h-3" /></button>
          {u.horoscope_disabled || !u.horoscope_access_until ? (
            <button data-testid={`btn-horo-grant-${u.id}`} onClick={() => grantHoroscope(u)} title="Otorgar horóscopo" className="text-xs px-2 py-1 rounded border border-fuchsia-500/30 text-fuchsia-300 hover:bg-fuchsia-500/10 flex items-center gap-1"><Sun className="w-3 h-3" /></button>
          ) : (
            <button data-testid={`btn-horo-revoke-${u.id}`} onClick={() => revokeHoroscope(u)} title="Pausar horóscopo" className="text-xs px-2 py-1 rounded border border-purple-500/30 text-purple-300 hover:bg-purple-500/10 flex items-center gap-1"><Moon className="w-3 h-3" /></button>
          )}
          {!u.vidente_access_until ? (
            <button data-testid={`btn-vidente-grant-${u.id}`} onClick={() => grantVidente(u)} title="Habilitar Vidente Virtual" className="text-xs px-2 py-1 rounded border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37]/10 flex items-center gap-1"><Mic className="w-3 h-3" /></button>
          ) : (
            <button data-testid={`btn-vidente-revoke-${u.id}`} onClick={() => revokeVidente(u)} title={`Revocar Vidente Virtual${u.vidente_access_until === "unlimited" ? " (ilimitado)" : ""}`} className="text-xs px-2 py-1 rounded border border-red-500/30 text-red-300 hover:bg-red-500/10 flex items-center gap-1"><MicOff className="w-3 h-3" /></button>
          )}
          <button data-testid={`btn-reset-memory-${u.id}`} onClick={() => resetSpiritualMemory(u)} title="Reiniciar memoria de Cristopher (Vidente + Lectura)" className="text-xs px-2 py-1 rounded border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/10 flex items-center gap-1"><Brain className="w-3 h-3" /></button>
          <button
            data-testid={`btn-offers-${u.id}`}
            onClick={() => setOfferConfigUser(u)}
            title={u.allow_offers ? `Ofertas habilitadas (max ${u.max_offer_pct || "?"}% / $${u.max_offer_amount || "?"})` : "Habilitar ofertas de descuento"}
            className={`text-xs px-2 py-1 rounded border flex items-center gap-1 ${u.allow_offers ? "border-emerald-500/50 text-emerald-300 bg-emerald-500/10" : "border-white/10 text-neutral-300 hover:border-emerald-500/40 hover:text-emerald-300"}`}
          >
            <Percent className="w-3 h-3" />
          </button>
          <label data-testid={`btn-avatar-${u.id}`} title="Subir avatar para este usuario" className="text-xs px-2 py-1 rounded border border-white/10 hover:border-sky-500/40 text-neutral-300 hover:text-sky-300 flex items-center gap-1 cursor-pointer">
            <Camera className="w-3 h-3" />
            <input type="file" accept="image/*" hidden onChange={(e) => uploadUserAvatar(u, e.target.files?.[0])} />
          </label>
          <button data-testid={`btn-delete-${u.id}`} onClick={() => deleteUser(u)} title="Eliminar usuario" className="text-xs px-2 py-1 rounded border border-red-500/30 text-red-400 hover:bg-red-500/10 flex items-center gap-1"><UserMinus className="w-3 h-3" /></button>
          {/* --- CONTACT SHORTCUTS --------------------------------- */}
          {u.phone && (() => {
            const cleanPhone = String(u.phone).replace(/[^\d+]/g, "");
            const waNum = cleanPhone.replace(/^\+/, "");
            const vcard = () => {
              const parts = (u.name || u.username || "Contacto").split(/\s+/);
              const first = parts[0] || "";
              const last = parts.slice(1).join(" ") || "";
              const vcf = [
                "BEGIN:VCARD",
                "VERSION:3.0",
                `N:${last};${first};;;`,
                `FN:${u.name || u.username || ""} (CrisAngel)`,
                "ORG:CrisAngel Astral",
                `TEL;TYPE=CELL:${cleanPhone}`,
                u.email ? `EMAIL:${u.email}` : "",
                u.visible_id ? `NOTE:ID: ${u.visible_id}` : "",
                "END:VCARD",
              ].filter(Boolean).join("\n");
              const blob = new Blob([vcf], { type: "text/vcard;charset=utf-8" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `${(u.name || u.username || "contacto").replace(/\s+/g, "_")}.vcf`;
              document.body.appendChild(a); a.click();
              setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 100);
            };
            return (
              <>
                <button data-testid={`btn-vcard-${u.id}`} onClick={vcard} title="Guardar contacto en el celular (vCard)" className="text-xs px-2 py-1 rounded border border-white/10 hover:border-blue-500/40 text-neutral-300 hover:text-blue-300 flex items-center gap-1"><UserPlus className="w-3 h-3" /></button>
                <a data-testid={`btn-wa-msg-${u.id}`} href={`https://wa.me/${waNum}`} target="_blank" rel="noreferrer" title="Enviar mensaje de WhatsApp" className="text-xs px-2 py-1 rounded border border-white/10 hover:border-green-500/40 text-neutral-300 hover:text-green-300 flex items-center gap-1"><MessageCircle className="w-3 h-3" /></a>
                <a data-testid={`btn-wa-call-${u.id}`} href={`whatsapp://call?phone=${cleanPhone}`} title="Llamada por WhatsApp" className="text-xs px-2 py-1 rounded border border-white/10 hover:border-green-500/40 text-neutral-300 hover:text-green-300 flex items-center gap-1"><PhoneCall className="w-3 h-3" /></a>
                <a data-testid={`btn-phone-${u.id}`} href={`tel:${cleanPhone}`} title="Llamada telefónica normal" className="text-xs px-2 py-1 rounded border border-white/10 hover:border-emerald-500/40 text-neutral-300 hover:text-emerald-300 flex items-center gap-1"><Phone className="w-3 h-3" /></a>
              </>
            );
          })()}
    </>
  );

  const usersById = React.useMemo(() => Object.fromEntries((users || []).map((u) => [u.id, u])), [users]);

  const togglePlanSignature = async (p, value) => {
    try {
      await axios.patch(`${API}/admin/payment-plans/${p.id}`, { allow_signature: !!value }, { headers: authHeader() });
      toast.success(value ? "Enlace de firma permitido para este servicio" : "Enlace de firma desactivado");
      load(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const openLegalDocs = async (plan) => {
    if (!plan.signature_id) return;
    setLegalDocsLoading(true);
    try {
      const r = await axios.get(`${API}/admin/signatures/${plan.signature_id}/legal-docs`, { headers: authHeader() });
      setLegalDocsOpen({ plan_id: plan.id, sid: plan.signature_id, items: r.data || [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setLegalDocsLoading(false);
    }
  };

  const downloadLegalPdf = async (sid, kind) => {
    try {
      const r = await axios.get(`${API}/admin/signatures/${sid}/legal/${kind}`, { headers: authHeader(), responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${kind}_${sid.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 100);
      toast.success("Descargado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  // Inline "Pagar promoción" — liquidates a plan applying its active promo.
  // Same endpoint as the Promotions tab; kept here so the Pagos/Trabajos
  // action bar can trigger it without leaving the tab.
  const liquidateWithPromoInline = (planId, promoId) => {
    const plan = plans.find((x) => x.id === planId);
    const promo = (plan?.promotions || []).find((pp) => pp.id === promoId);
    if (!plan || !promo) return toast.error("Promoción no encontrada");
    setPromoLiquidateTarget({ plan, promo });
  };

  const confirmLiquidateWithPromo = async (reflected) => {
    const { plan: targetPlan, promo } = promoLiquidateTarget || {};
    if (!targetPlan || !promo) return;
    try {
      const r = await axios.post(
        `${API}/admin/payment-plans/${targetPlan.id}/promotions/${promo.id}/liquidate`,
        { reflected },
        { headers: authHeader() },
      );
      toast.success("¡Plan liquidado con la promoción!");
      setPromoLiquidateTarget(null);
      const receiptInfo = r.data?.receipt;
      if (receiptInfo?.share_path) {
        const clientUser = users.find((x) => x.id === targetPlan?.user_id);
        const backend = process.env.REACT_APP_BACKEND_URL;
        setReceiptShare({
          receipt_number: receiptInfo.receipt_number,
          share_url: `${backend}${receiptInfo.share_path}`,
          image_url: `${backend}${receiptInfo.image_path || receiptInfo.share_path}`,
          message_id: receiptInfo.message_id,
          client_name: clientUser?.name || clientUser?.username || targetPlan.user_name || "cliente",
          client_phone: clientUser?.phone || "",
          service_name: targetPlan?.service_name || "trabajo",
          amount: Number(r.data?.installment_amount || 0),
          currency: targetPlan?.currency || "MXN",
          fully_paid: true,
          reflected,
          pending_booking_plan: targetPlan ? { ...targetPlan, fully_paid: true, status: "paid" } : null,
        });
      }
      await load(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo liquidar");
    }
  };

  const markInstallmentReflected = async (plan, inst) => {
    try {
      const r = await axios.post(
        `${API}/admin/payment-plans/${plan.id}/installments/${inst.id}/reflected`,
        { reflected: true, notify: true },
        { headers: authHeader() },
      );
      toast.success("Abono marcado como reflejado — cliente avisado");
      // Keep the open modal in sync
      const updatedInsts = (plan.installments || []).map((x) => (x.id === inst.id ? r.data.installment : x));
      setInstallPlan((prev) => (prev && prev.id === plan.id ? { ...prev, installments: updatedInsts } : prev));
      if (r.data.message) {
        setReflectedShare({
          message: r.data.message,
          client_name: r.data.client_name,
          client_phone: r.data.client_phone,
          amount: inst.amount,
          currency: plan.currency,
          service_name: r.data.service_name,
        });
      }
      await load(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo marcar como reflejado");
    }
  };

  const downloadClientSchedulePdf = async (plan) => {
    try {
      const r = await axios.get(`${API}/admin/payment-plans/${plan.id}/client-schedule.pdf`, { headers: authHeader(), responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `plan_pagos_${(plan.user_name || "cliente").replace(/\s+/g, "_")}_${plan.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 100);
      toast.success("PDF de pagos generado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const openTestimonialLinkModal = async (apt) => {
    setTestimonialApt(apt);
    setTestimonialInfo(null);
    setTestimonialBusy(true);
    try {
      const r = await axios.get(`${API}/admin/appointments/${apt.id}/testimonial`, { headers: authHeader() });
      setTestimonialInfo(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar el estado");
    } finally {
      setTestimonialBusy(false);
    }
  };

  const generateTestimonialLink = async () => {
    if (!testimonialApt || testimonialBusy) return;
    setTestimonialBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/appointments/${testimonialApt.id}/testimonial-link`,
        {}, { headers: authHeader() },
      );
      const base = window.location.origin;
      setTestimonialInfo({
        has_link: true,
        url: `${base}${r.data.url}`,
        token: r.data.token,
        used: r.data.used,
        expires_at: r.data.expires_at,
      });
      toast.success(r.data.reused ? "Enlace ya existente" : "Enlace generado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al generar enlace");
    } finally {
      setTestimonialBusy(false);
    }
  };

  const sendTestimonialLinkWhatsApp = () => {
    if (!testimonialInfo?.url || !testimonialApt) return;
    const clientName = testimonialApt.user_name || testimonialApt.user_visible_id;
    const svc = testimonialApt.service_name || "tu sesión";
    const msg = encodeURIComponent(
      `Hola ${clientName}, muchas gracias por tu confianza. Aquí puedes compartir tu experiencia sobre ${svc} y tu testimonio se publicará junto a los demás: ${testimonialInfo.url} Bendiciones.`,
    );
    const phone = testimonialApt.user_phone || "";
    window.open(phone ? `https://wa.me/${phone.replace(/\D/g, "")}?text=${msg}` : `https://wa.me/?text=${msg}`, "_blank");
  };

  const copyTestimonialLink = async () => {
    if (!testimonialInfo?.url) return;
    try {
      await navigator.clipboard.writeText(testimonialInfo.url);
      toast.success("Enlace copiado");
    } catch { toast.error("No se pudo copiar"); }
  };

  const deleteAppointmentTestimonial = async () => {
    if (!testimonialApt) return;
    if (!window.confirm("¿Eliminar el testimonio publicado por el cliente para esta cita? Esta acción NO se puede deshacer.")) return;
    setTestimonialBusy(true);
    try {
      await axios.delete(`${API}/admin/appointments/${testimonialApt.id}/testimonial`, { headers: authHeader() });
      toast.success("Testimonio eliminado");
      setTestimonialInfo({ has_link: false });
      // Also update the local appointments state so the badge disappears.
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar");
    } finally {
      setTestimonialBusy(false);
    }
  };

  const openReceiptGallery = async (plan) => {
    setGalleryPlan(plan);
    setGalleryItems([]);
    setGalleryLoading(true);
    setGalleryThumbs({});
    setGalleryFilterInst("all");
    try {
      const r = await axios.get(`${API}/admin/payment-plans/${plan.id}/receipts`, { headers: authHeader() });
      setGalleryItems(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar la galería");
    } finally {
      setGalleryLoading(false);
    }
  };
  const closeReceiptGallery = () => {
    setGalleryPlan(null);
    setGalleryItems([]);
    setGalleryThumbs({});
    setGalleryZoom(null);
    setGalleryFilterInst("all");
  };
  const loadReceiptData = async (receipt) => {
    if (!galleryPlan) return null;
    try {
      const r = await axios.get(
        `${API}/admin/payment-plans/${galleryPlan.id}/receipts/${receipt.id}/file`,
        { headers: authHeader() },
      );
      return r.data;
    } catch (e) {
      toast.error("No se pudo cargar el comprobante");
      return null;
    }
  };
  const loadThumb = async (receipt) => {
    if (galleryThumbs[receipt.id] || !receipt.is_image) return;
    const data = await loadReceiptData(receipt);
    if (!data) return;
    setGalleryThumbs((prev) => ({
      ...prev,
      [receipt.id]: `data:${data.mime_type};base64,${data.data_b64}`,
    }));
  };
  const openZoom = async (receipt) => {
    setGalleryZoomLoading(true);
    try {
      const data = await loadReceiptData(receipt);
      if (!data) return;
      setGalleryZoom({
        receipt_id: receipt.id,
        data_b64: data.data_b64,
        mime_type: data.mime_type,
        filename: receipt.filename,
        installment_id: receipt.installment_id,
        note: receipt.note,
        status: receipt.status,
        uploaded_at: receipt.uploaded_at,
      });
    } finally {
      setGalleryZoomLoading(false);
    }
  };

  const setZoomReceiptStatus = async (newStatus) => {
    if (!galleryPlan || !galleryZoom?.receipt_id) return;
    if (galleryStatusBusy) return;
    setGalleryStatusBusy(true);
    try {
      await axios.patch(
        `${API}/admin/payment-plans/${galleryPlan.id}/receipts/${galleryZoom.receipt_id}/status`,
        { status: newStatus },
        { headers: authHeader() },
      );
      // Optimistic update — the modal stays open, only the status pill flips.
      setGalleryZoom((prev) => (prev ? { ...prev, status: newStatus } : prev));
      setGalleryItems((prev) =>
        prev.map((it) => (it.id === galleryZoom.receipt_id ? { ...it, status: newStatus } : it))
      );
      toast.success(
        newStatus === "confirmed" ? "Pago confirmado y usuario notificado"
        : newStatus === "rejected" ? "Comprobante rechazado y usuario notificado"
        : "Status actualizado",
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar");
    } finally {
      setGalleryStatusBusy(false);
    }
  };

  const downloadPlanReportPdf = async (plan) => {
    if (!plan) return;
    try {
      const r = await axios.get(
        `${API}/admin/payment-plans/${plan.id}/report.pdf`,
        { headers: authHeader(), responseType: "blob" },
      );
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `reporte_${(plan.service_name || "plan").replace(/\s+/g, "_")}_${plan.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 100);
      toast.success("Reporte PDF descargado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo generar el reporte");
    }
  };

  // profile request review modal
  const [reviewReq, setReviewReq] = useState(null);
  const [reviewApproved, setReviewApproved] = useState({});
  const [reviewNote, setReviewNote] = useState("");

  // grant reading modal
  const [grantFor, setGrantFor] = useState(null); // visible_id
  const [grantForm, setGrantForm] = useState({ reading_type: "normal", duration_minutes: "", custom_focus: "" });

  // reading detail modal
  const [openReading, setOpenReading] = useState(null);

  // receipt preview
  const [openReceipt, setOpenReceipt] = useState(null);

  // send-receipt modal (admin-generated payment receipt image)
  const [sendReceiptTo, setSendReceiptTo] = useState(null);

  // Fast lookup by user_id / visible_id → { name, username, phone, email, ... }
  // Used by the Citas + Pagos filters to search by any of these fields even
  // though the endpoints themselves don't ship them per-row.
  const userIndex = React.useMemo(() => {
    const byId = new Map();
    const byVisible = new Map();
    for (const u of users) {
      if (u.id) byId.set(u.id, u);
      if (u.visible_id) byVisible.set(u.visible_id.toUpperCase(), u);
    }
    return { byId, byVisible };
  }, [users]);

  const matchesUserQuery = React.useCallback(
    (row, query) => {
      const q = (query || "").trim().toLowerCase();
      if (!q) return true;
      const u = userIndex.byId.get(row.user_id) || userIndex.byVisible.get((row.user_visible_id || "").toUpperCase()) || {};
      return [
        row.user_visible_id,
        row.user_name,
        u.name, u.username, u.email, u.phone, u.visible_id,
      ].some((x) => (x || "").toString().toLowerCase().includes(q));
    },
    [userIndex],
  );

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [s, u, m, c, a, p, act, pr, rs] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: authHeader() }),
        axios.get(`${API}/admin/all-users`, { headers: authHeader() }),
        axios.get(`${API}/admin/private-messages`, { headers: authHeader() }),
        axios.get(`${API}/admin/chat-sessions`, { headers: authHeader() }),
        axios.get(`${API}/admin/appointments`, { headers: authHeader() }),
        axios.get(`${API}/admin/payment-plans`, { headers: authHeader() }),
        axios.get(`${API}/admin/activity`, { headers: authHeader() }),
        axios.get(`${API}/admin/profile-requests`, { headers: authHeader() }),
        axios.get(`${API}/admin/reading-sessions`, { headers: authHeader() }),
      ]);
      setStats(s.data); setUsers(u.data); setMessages(m.data);
      setChats(c.data); setAppts(a.data); setPlans(p.data); setActivity(act.data);
      setProfileReqs(pr.data); setReadings(rs.data);
      // Fire-and-forget: fetch testimonials for any user_id in plans we
      // haven't cached yet. Populates the "🌟 Testimonio" preview badge
      // in the Pagos tab without blocking the initial render.
      try {
        const uniqueUids = Array.from(new Set((p.data || []).map((pl) => pl.user_id).filter(Boolean)));
        const missing = uniqueUids.filter((uid) => !(uid in userTestimonialsMap));
        if (missing.length) {
          const entries = await Promise.all(missing.map(async (uid) => {
            try {
              const r = await axios.get(`${API}/admin/users/${uid}/testimonial`, { headers: authHeader() });
              return [uid, Array.isArray(r.data) ? r.data : []];
            } catch { return [uid, []]; }
          }));
          setUserTestimonialsMap((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
        }
      } catch { /* non-critical */ }
    } catch (e) {
      if (e?.response?.status === 401) { logout(); navigate("/ingresar"); }
      else if (!silent) toast.error("Error cargando datos");
    } finally { if (!silent) setLoading(false); }
  }, [authHeader, logout, navigate]);

  useEffect(() => { load(false); }, [load]);

  // Debounced silent reload to coalesce bursty SSE events
  const reloadTimerRef = useRef(null);
  const triggerPulse = useCallback((type, data) => {
    window.dispatchEvent(new CustomEvent("admin:pulse", { detail: { type, data: data || {} } }));
    if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
    reloadTimerRef.current = setTimeout(() => { load(true); }, 400);
  }, [load]);

  // Real-time admin updates via SSE — refresh on ANY admin-relevant event
  useRealtimeEvents({
    message: (d) => triggerPulse("message", d),
    notification: (d) => triggerPulse("notification", d),
    profile_request: (d) => triggerPulse("profile_request", d),
    receipt_uploaded: (d) => triggerPulse("receipt_uploaded", d),
    appointment_changed: (d) => triggerPulse("appointment_changed", d),
    admin_refresh: (d) => triggerPulse("admin_refresh", d),
    blocked_attempt: (d) => {
      toast.error(`🚫 Intento de acceso bloqueado: ${d?.identifier || `IP ${d?.ip || "?"}`}`, {
        description: "Revisa Accesos → Intentos bloqueados para ver IP, dispositivo y cliente.",
        duration: 10000,
      });
      triggerPulse("blocked_attempt", d);
    },
    ai_escalation: (d) => {
      toast(`🙋 ${d?.user_name || "Un cliente"} pide atención personal`, { description: d?.reason || "", duration: 12000 });
      triggerPulse("message", d);
    },
  });

  // Quick actions triggered from the admin chat dock buttons
  useEffect(() => {
    const onGrant = (e) => {
      const d = e.detail || {};
      setGrantFor(d.visible_id || "");
      setGrantForm((f) => ({ ...f, user_visible_id: d.visible_id || "" }));
      toast.info(`Otorgando lectura a ${d.user_name || "cliente"}`);
    };
    const onAppt = (e) => {
      const d = e.detail || {};
      setApt((a) => ({ ...a, user_visible_id: d.visible_id || "" }));
      setTab("appts");
      setTimeout(() => document.querySelector('[data-testid="apt-section"]')?.scrollIntoView({ behavior: "smooth" }), 80);
      toast.info(`Registrando cita para ${d.user_name || "cliente"}`);
    };
    const onPay = (e) => {
      const d = e.detail || {};
      setTab("plans");
      setPlanForm((p) => ({ ...p, user_visible_id: d.visible_id || "" }));
      setTimeout(() => document.querySelector('[data-testid="pay-section"]')?.scrollIntoView({ behavior: "smooth" }), 80);
      toast.info(`Registrando pago para ${d.user_name || "cliente"}`);
    };
    const onDossier = (e) => {
      const d = e.detail || {};
      if (d.user_id) setDossierUserId(d.user_id);
    };
    window.addEventListener("admin:quickGrant", onGrant);
    window.addEventListener("admin:quickAppt", onAppt);
    window.addEventListener("admin:quickPay", onPay);
    window.addEventListener("admin:openDossier", onDossier);
    return () => {
      window.removeEventListener("admin:quickGrant", onGrant);
      window.removeEventListener("admin:quickAppt", onAppt);
      window.removeEventListener("admin:quickPay", onPay);
      window.removeEventListener("admin:openDossier", onDossier);
    };
  }, []);

  const toggleBlock = async (uid, blocked) => {
    try {
      await axios.patch(`${API}/admin/users/${uid}/block`, { blocked }, { headers: authHeader() });
      setUsers((us) => us.map((x) => x.id === uid ? { ...x, blocked } : x));
      toast.success(blocked ? "Usuario bloqueado" : "Usuario desbloqueado");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const doResetPassword = async () => {
    if (!resetPw || resetPw.length < 6) return toast.error("Min 6 caracteres");
    try {
      await axios.post(`${API}/admin/users/${resetTarget.id}/reset-password`, { new_password: resetPw }, { headers: authHeader() });
      toast.success("Contraseña reseteada y usuario notificado");
      setResetTarget(null); setResetPw("");
    } catch { toast.error("Error"); }
  };

  const revealPassword = async (u) => {
    // Toggle off if already revealed
    if (revealedPw[u.id] !== undefined) {
      setRevealedPw((m) => { const c = { ...m }; delete c[u.id]; return c; });
      return;
    }
    try {
      const { data } = await axios.get(`${API}/admin/users/${u.id}/password`, { headers: authHeader() });
      if (!data.captured) {
        toast.info(data.hint || "Aún no se capturó la contraseña.");
        return;
      }
      setRevealedPw((m) => ({ ...m, [u.id]: data.password }));
    } catch { toast.error("No se pudo obtener la contraseña"); }
  };

  const revealLocation = async (u) => {
    if (revealedGeo[u.id] !== undefined) {
      setRevealedGeo((m) => { const c = { ...m }; delete c[u.id]; return c; });
      return;
    }
    try {
      const { data } = await axios.get(`${API}/admin/users/${u.id}/location`, { headers: authHeader() });
      if (!data.geo) {
        toast.info("Este usuario no compartió su ubicación al registrarse.");
        return;
      }
      setRevealedGeo((m) => ({ ...m, [u.id]: data.geo }));
    } catch { toast.error("No se pudo obtener la ubicación"); }
  };

  const sendReply = async () => {
    if (!replyText.trim()) return;
    try {
      await axios.post(`${API}/admin/users/${replyTo.id}/messages`, { body: replyText }, { headers: authHeader() });
      toast.success("Mensaje enviado");
      setReplyTo(null); setReplyText("");
      load();
    } catch { toast.error("Error"); }
  };

  const createAppointment = async () => {
    try {
      await axios.post(`${API}/admin/appointments`, apt, { headers: authHeader() });
      toast.success("Cita agendada y notificada al usuario");
      setApt({ user_visible_id: "", service_id: "", scheduled_date: "", scheduled_time: "", notes: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  // ------------- Calendar helpers (Bloque 3) -------------
  // Convert MX-local (America/Mexico_City = UTC-6, no DST since 2022) date + time
  // to UTC. Simplification: MX-Sonora already has fixed UTC-7; we accept -6 as
  // a conservative default for the app's stated timezone.
  const _apptToUtc = (a) => {
    const d = a?.scheduled_date;
    const t = a?.scheduled_time || "09:00";
    if (!d || !t) return null;
    // Interpret as MX (-06:00). Result: Date object in UTC.
    const iso = `${d}T${t}:00-06:00`;
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return null;
    return dt;
  };

  const _apptToGoogleUrl = (a) => {
    const start = _apptToUtc(a);
    if (!start) return null;
    const end = new Date(start.getTime() + 60 * 60 * 1000); // 1h duration
    const fmt = (dt) => dt.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
    const title = encodeURIComponent(`Cita CrisAngel — ${a.service_name || "Servicio"} (${a.user_name || a.user_visible_id})`);
    const details = encodeURIComponent(
      `Cita agendada por CrisAngel Astral.\nUsuario: ${a.user_name || "-"} (${a.user_visible_id})\nServicio: ${a.service_name || "-"}\n${a.notes ? `Notas: ${a.notes}\n` : ""}Zona: America/Mexico_City`
    );
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${fmt(start)}/${fmt(end)}&details=${details}&sf=true&output=xml`;
  };

  const downloadApptIcs = (a) => {
    const start = _apptToUtc(a);
    if (!start) return toast.error("Fecha/hora inválida");
    const end = new Date(start.getTime() + 60 * 60 * 1000);
    const fmt = (dt) => dt.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
    const escape = (s) => String(s || "").replace(/([,;])/g, "\\$1").replace(/\r?\n/g, "\\n");
    const uid = `apt-${a.id}@crisangelastral.com`;
    // VALARM: 1 hour before (matches user request)
    const ics = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//CrisAngel Astral//Appointments//ES",
      "CALSCALE:GREGORIAN",
      "METHOD:PUBLISH",
      "BEGIN:VEVENT",
      `UID:${uid}`,
      `DTSTAMP:${fmt(new Date())}`,
      `DTSTART:${fmt(start)}`,
      `DTEND:${fmt(end)}`,
      `SUMMARY:${escape(`Cita CrisAngel — ${a.service_name || "Servicio"}`)}`,
      `DESCRIPTION:${escape(`Cliente: ${a.user_name || "-"} (${a.user_visible_id})${a.notes ? `\nNotas: ${a.notes}` : ""}`)}`,
      "STATUS:CONFIRMED",
      "BEGIN:VALARM",
      "TRIGGER:-PT1H",
      "ACTION:DISPLAY",
      "DESCRIPTION:Tu cita CrisAngel es en 1 hora",
      "END:VALARM",
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\r\n");
    const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `cita_${(a.user_visible_id || "cliente")}_${a.scheduled_date}.ics`;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => { URL.revokeObjectURL(url); link.remove(); }, 100);
    toast.success("Descargado — ábrelo desde tu Android/iPhone/Outlook para agregar el recordatorio");
  };

  const openChatDetail = async (sid) => {
    try {
      const r = await axios.get(`${API}/admin/chat-sessions/${sid}`, { headers: authHeader() });
      setOpenChat(r.data);
    } catch { toast.error("Error"); }
  };

  // === Appointment actions ===
  const openEditApt = (a) => {
    setEditApt(a);
    setAptEdit({ service_id: a.service_id, scheduled_date: a.scheduled_date, scheduled_time: a.scheduled_time, notes: a.notes || "" });
    setAptComment("");
  };

  const saveEditApt = async () => {
    try {
      const payload = { ...aptEdit };
      if (aptComment.trim()) payload.admin_comment = aptComment.trim();
      await axios.patch(`${API}/admin/appointments/${editApt.id}`, payload, { headers: authHeader() });
      toast.success("Cita actualizada y usuario notificado");
      setEditApt(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const setApptStatus = async (aId, status, label) => {
    try {
      await axios.patch(`${API}/admin/appointments/${aId}`, { status }, { headers: authHeader() });
      toast.success(`Cita marcada como ${label}. Usuario notificado.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const deleteAppt = async (aId) => {
    if (!window.confirm("¿Eliminar esta cita? Se notificará al usuario.")) return;
    try {
      await axios.delete(`${API}/admin/appointments/${aId}`, { headers: authHeader() });
      toast.success("Cita eliminada");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const addApptComment = async () => {
    if (!aptComment.trim() || !editApt) return;
    try {
      await axios.patch(`${API}/admin/appointments/${editApt.id}`, { admin_comment: aptComment.trim() }, { headers: authHeader() });
      toast.success("Comentario interno agregado");
      setAptComment("");
      // refresh editApt
      const r = await axios.get(`${API}/admin/appointments`, { headers: authHeader() });
      setAppts(r.data);
      setEditApt(r.data.find((x) => x.id === editApt.id));
    } catch { toast.error("Error"); }
  };

  // === Payment plan actions ===
  const createPlan = async () => {
    const payload = {
      user_visible_id: planForm.user_visible_id,
      agreed_price: Number(planForm.agreed_price),
      currency: planForm.currency || "MXN",
      payment_promise_date: planForm.payment_promise_date || null,
      notes: planForm.notes || null,
      installments_count: Math.max(1, Number(planForm.installments_count) || 1),
      frequency: planForm.frequency || "single",
      custom_interval_days: planForm.frequency === "custom"
        ? Math.max(1, Number(planForm.custom_interval_days) || 0)
        : null,
    };
    if (planMode === "service") {
      if (!planForm.service_id) return toast.error("Selecciona un servicio");
      payload.service_id = planForm.service_id;
    } else {
      if (!planForm.custom_name.trim()) return toast.error("Nombre personalizado requerido");
      payload.custom_name = planForm.custom_name.trim();
      payload.custom_category = planForm.custom_category || "otro";
      payload.register_custom = planForm.register_custom !== false;
    }
    if (planForm.allow_signature !== null && planForm.allow_signature !== undefined) payload.allow_signature = !!planForm.allow_signature;
    if (!payload.user_visible_id || !payload.agreed_price) return toast.error("Datos faltantes");
    try {
      const r = await axios.post(`${API}/admin/payment-plans`, payload, { headers: authHeader() });
      const createdPlan = r?.data?.plan || null;
      // Si el precio pactado es 0 (o el pago llegó completo de una vez), el
      // plan queda liquidado desde el nacimiento — en ese caso sí abrimos el
      // modal. En cualquier otro caso, el enlace se generará más tarde al
      // registrar el último abono.
      const alreadyPaid = createdPlan?.fully_paid || createdPlan?.status === "paid"
        || (Number(createdPlan?.paid_total || 0) >= Number(createdPlan?.agreed_price || 0) - 0.01
            && Number(createdPlan?.agreed_price || 0) > 0);
      if (alreadyPaid && createdPlan?.id) {
        toast.success("Servicio registrado y ya liquidado — enlace listo");
        setBookingLinkVariant("created");
        setBookingLinkPlan(createdPlan);
      } else {
        toast.success("Servicio registrado y usuario notificado");
      }
      setPlanForm(EMPTY_PLAN_FORM);
      load();
      if (planMode === "custom" && payload.register_custom) loadPlanServices();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const openInstall = (p) => {
    setInstallPlan(p);
    setInstallForm({ amount: "", method: "", notes: "", receipt_file: null, reflected: true });
  };

  const _readAsB64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("read_error"));
    reader.onload = () => {
      const s = String(reader.result || "");
      const idx = s.indexOf(",");
      resolve(idx >= 0 ? s.slice(idx + 1) : s);
    };
    reader.readAsDataURL(file);
  });

  const addInstallment = async () => {
    if (installBusy) return; // double-click guard
    const amt = Number(installForm.amount);
    if (!amt || amt <= 0) return toast.error("Monto inválido");
    setInstallBusy(true);
    try {
      const currentPlan = installPlan;
      // Idempotency key: server rejects a second POST with the same key
      // even if the client managed to retry (e.g. flaky network).
      const dedupKey = `${currentPlan.id}:${amt}:${installForm.method || ""}:${Date.now()}`;
      const body = { amount: amt, method: installForm.method, notes: installForm.notes, client_dedup_key: dedupKey, reflected: installForm.reflected !== false };
      if (installForm.receipt_file) {
        try {
          body.receipt_data_b64 = await _readAsB64(installForm.receipt_file);
          body.receipt_mime = installForm.receipt_file.type || "image/jpeg";
          body.receipt_filename = installForm.receipt_file.name || "comprobante";
        } catch {
          toast.error("No se pudo leer el comprobante");
          setInstallBusy(false);
          return;
        }
      }
      const r = await axios.post(`${API}/admin/payment-plans/${currentPlan.id}/installments`,
        body, { headers: authHeader() });
      const fullyPaid = !!r.data.fully_paid;
      toast.success(fullyPaid ? "✅ ¡Servicio liquidado! Usuario notificado." : "Abono registrado y usuario notificado");
      // Prepare WhatsApp share flow if a receipt was auto-generated
      const receiptInfo = r.data.receipt;
      if (receiptInfo?.share_path) {
        const clientUser = users.find((x) => x.id === currentPlan.user_id);
        const backend = process.env.REACT_APP_BACKEND_URL;
        setReceiptShare({
          receipt_number: receiptInfo.receipt_number,
          // share_url points to the HTML preview page (OG tags → WhatsApp
          // rich preview card). image_url points to the raw PNG (download).
          share_url: `${backend}${receiptInfo.share_path}`,
          image_url: `${backend}${receiptInfo.image_path || receiptInfo.share_path}`,
          message_id: receiptInfo.message_id,
          client_name: clientUser?.name || clientUser?.username || "cliente",
          client_phone: clientUser?.phone || "",
          service_name: currentPlan.service_name,
          amount: Number(body.amount || 0),
          currency: currentPlan.currency || "MXN",
          fully_paid: fullyPaid,
          reflected: body.reflected,
          // If the plan was liquidated, remember the plan so we can auto-open
          // the booking-link modal AFTER the admin closes the share panel.
          pending_booking_plan: fullyPaid && currentPlan?.id ? currentPlan : null,
        });
      } else if (fullyPaid && currentPlan?.id) {
        // No receipt to share (auto_receipt=false) but plan is paid — open booking link directly.
        setBookingLinkVariant("installment");
        setBookingLinkPlan({ ...currentPlan, fully_paid: true, status: "paid" });
      }
      setInstallPlan(null);
      setInstallForm({ amount: "", method: "", notes: "", receipt_file: null, reflected: true });
      load();
      // El enlace de agendado se abre SOLO al liquidar el plan Y sin receipt-share
      // activo. Cuando hay receipt-share, se abrirá al cerrar ese modal.
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.message) {
        toast.error(detail.message);
      } else {
        toast.error(typeof detail === "string" ? detail : "Error");
      }
    } finally {
      setInstallBusy(false);
    }
  };

  const deleteInstallment = async (pid, iid) => {
    if (!window.confirm("¿Eliminar este abono? Se recalculará el saldo.")) return;
    try {
      await axios.delete(`${API}/admin/payment-plans/${pid}/installments/${iid}`, { headers: authHeader() });
      toast.success("Abono eliminado — total recalculado");
      const r = await axios.get(`${API}/admin/payment-plans`, { headers: authHeader() });
      setPlans(r.data);
      if (installPlan) setInstallPlan(r.data.find((p) => p.id === pid));
    } catch { toast.error("Error"); }
  };

  const openEditInstallment = (planObj, inst) => {
    setEditInstall({ plan_id: planObj.id, currency: planObj.currency, id: inst.id, original: inst });
    setEditInstallForm({
      amount: inst.amount ?? "",
      method: inst.method || "",
      notes: inst.notes || "",
    });
  };

  const saveEditInstallment = async () => {
    if (editInstallBusy || !editInstall) return;
    const amt = Number(editInstallForm.amount);
    if (!amt || amt <= 0) return toast.error("Monto inválido");
    setEditInstallBusy(true);
    try {
      await axios.patch(
        `${API}/admin/payment-plans/${editInstall.plan_id}/installments/${editInstall.id}`,
        { amount: amt, method: editInstallForm.method, notes: editInstallForm.notes },
        { headers: authHeader() },
      );
      toast.success("Abono actualizado — total recalculado");
      const r = await axios.get(`${API}/admin/payment-plans`, { headers: authHeader() });
      setPlans(r.data);
      if (installPlan) setInstallPlan(r.data.find((p) => p.id === editInstall.plan_id));
      setEditInstall(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar el abono");
    } finally {
      setEditInstallBusy(false);
    }
  };

  const openEditPlan = (p) => {
    setEditPlan(p);
    setPlanEdit({ agreed_price: p.agreed_price, payment_promise_date: p.payment_promise_date || "", notes: p.notes || "", status: p.status });
    setPlanComment("");
  };

  const saveEditPlan = async () => {
    try {
      const payload = { ...planEdit };
      if (payload.agreed_price !== undefined) payload.agreed_price = Number(payload.agreed_price);
      if (planComment.trim()) payload.admin_comment = planComment.trim();
      await axios.patch(`${API}/admin/payment-plans/${editPlan.id}`, payload, { headers: authHeader() });
      toast.success("Servicio actualizado");
      setEditPlan(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const cancelPlan = async (pid) => {
    if (!window.confirm("¿Cancelar este servicio? El usuario será notificado.")) return;
    try {
      await axios.patch(`${API}/admin/payment-plans/${pid}`, { status: "cancelled" }, { headers: authHeader() });
      toast.success("Servicio cancelado y usuario notificado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const deletePlan = async (pid) => {
    if (!window.confirm("¿Eliminar permanentemente este servicio? El usuario será notificado.")) return;
    try {
      await axios.delete(`${API}/admin/payment-plans/${pid}`, { headers: authHeader() });
      toast.success("Servicio eliminado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  // === User actions ===
  const deleteUser = async (u) => {
    if (!window.confirm(`¿Eliminar a ${u.name} permanentemente? Se borrarán sus citas, pagos, mensajes y lecturas.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${u.id}`, { headers: authHeader() });
      toast.success("Usuario eliminado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const grantHoroscope = async (u) => {
    const daysStr = window.prompt(`Otorgar acceso a horóscopos diarios para ${u.name}\n\nDías a sumar (30 para lectura, 365 para trabajo):`, "30");
    if (!daysStr) return;
    const days = Number(daysStr);
    if (!days || days < 1) return toast.error("Días inválidos");
    try {
      await axios.post(`${API}/admin/users/${u.id}/horoscope/grant`, { days, reason: "manual" }, { headers: authHeader() });
      toast.success(`+${days} días de horóscopo activados`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const revokeHoroscope = async (u) => {
    if (!window.confirm(`¿Pausar acceso a horóscopos para ${u.name}?`)) return;
    try {
      await axios.post(`${API}/admin/users/${u.id}/horoscope/revoke`, {}, { headers: authHeader() });
      toast.success("Horóscopos pausados");
      load();
    } catch { toast.error("Error"); }
  };

  const grantVidente = async (u) => {
    const hoursStr = window.prompt(
      `🎙️ Habilitar Vidente Virtual para ${u.name}\n\n` +
      `Horas de acceso (0 = ilimitado). Acepta decimales (ej. 0.5 = 30 min).\n\n` +
      `⏳ El tiempo comenzará a contar cuando el usuario hable con la IA por PRIMERA vez, no ahora.`,
      "24"
    );
    if (hoursStr === null) return;
    const hours = Number(hoursStr);
    if (Number.isNaN(hours) || hours < 0) return toast.error("Horas inválidas");
    try {
      await axios.post(
        `${API}/admin/vidente/grant`,
        { user_visible_id: u.visible_id, duration_minutes: Math.round(hours * 60) },
        { headers: authHeader() }
      );
      toast.success(
        hours === 0
          ? "Acceso ilimitado a Vidente Virtual"
          : `${Math.round(hours * 60)} min listos — el contador inicia con su primera interacción`
      );
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const revokeVidente = async (u) => {
    if (!window.confirm(`¿Revocar acceso a Vidente Virtual para ${u.name}?`)) return;
    try {
      await axios.post(
        `${API}/admin/vidente/revoke`,
        { user_visible_id: u.visible_id },
        { headers: authHeader() }
      );
      toast.success("Vidente Virtual revocada");
      load();
    } catch { toast.error("Error"); }
  };

  const resetSpiritualMemory = async (u) => {
    if (!window.confirm(
      `🧠 Reiniciar memoria de Cristopher para ${u.name}?\n\n` +
      `Se borrarán: nombre completo, fecha de nacimiento, signo, motivos, hechos aprendidos, predicciones y resumen.\n\n` +
      `Cristopher empezará desde cero la próxima vez que hable con este cliente.`
    )) return;
    try {
      await axios.delete(
        `${API}/admin/spiritual-profile/${u.id}`,
        { headers: authHeader() }
      );
      toast.success("Memoria de Cristopher reiniciada");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const uploadUserAvatar = async (u, file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error("Imagen muy grande (máx 2MB)");
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const b64 = (reader.result || "").toString().split(",")[1] || "";
        await axios.post(`${API}/admin/users/${u.id}/profile-pic`, { data_b64: b64, mime_type: file.type }, { headers: authHeader() });
        toast.success("Avatar actualizado");
        try { window.__bumpAvatarCache?.(u.id); } catch { /* noop */ }
        load();
      };
      reader.readAsDataURL(file);
    } catch { toast.error("Error"); }
  };

  const quickAppt = (u) => {
    setApt({ user_visible_id: u.visible_id, service_id: "", scheduled_date: "", scheduled_time: "", notes: "" });
    setTab("appts");
    toast.info(`ID ${u.visible_id} pre-rellenado en formulario de citas`);
  };

  const quickPayment = (u) => {
    setPlanForm((f) => ({ ...f, user_visible_id: u.visible_id }));
    setTab("plans");
    toast.info(`ID ${u.visible_id} pre-rellenado en formulario de pagos`);
  };

  const quickGrant = (u) => {
    setGrantFor(u.visible_id);
    setGrantForm({ reading_type: "normal", duration_minutes: "", custom_focus: "", draws_per_phase: {} });
  };

  // === Profile requests ===
  const openReview = (req) => {
    setReviewReq(req);
    const approved = {};
    Object.keys(req.requested_changes || {}).forEach((k) => { approved[k] = true; });
    setReviewApproved(approved);
    setReviewNote("");
  };

  const submitReview = async () => {
    const approved_fields = Object.entries(reviewApproved).filter(([_, v]) => v).map(([k]) => k);
    const rejected_fields = Object.entries(reviewApproved).filter(([_, v]) => !v).map(([k]) => k);
    try {
      await axios.post(`${API}/admin/profile-requests/${reviewReq.id}/decide`,
        { approved_fields, rejected_fields, admin_note: reviewNote || null },
        { headers: authHeader() });
      toast.success("Decisión registrada y usuario notificado");
      setReviewReq(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  // === Reading sessions ===
  const submitGrant = async () => {
    try {
      await axios.post(`${API}/admin/reading/grant`, {
        user_visible_id: grantFor,
        reading_type: grantForm.reading_type,
        duration_minutes: Number(grantForm.duration_minutes) || null,
        custom_focus: grantForm.custom_focus || null,
        draws_per_phase: grantForm.draws_per_phase || null,
      }, { headers: authHeader() });
      toast.success("Lectura habilitada y usuario notificado");
      setGrantFor(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const resetReading = async (sid) => {
    if (!window.confirm("¿Reiniciar la lectura? El cliente podrá iniciarla de nuevo.")) return;
    try {
      await axios.post(`${API}/admin/reading-sessions/${sid}/reset`, {}, { headers: authHeader() });
      toast.success("Lectura reiniciada");
      load();
      setOpenReading(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const changeReadingType = async (sid, type) => {
    try {
      await axios.patch(`${API}/admin/reading-sessions/${sid}`, { reading_type: type }, { headers: authHeader() });
      toast.success("Tipo de lectura cambiado y usuario notificado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const cancelReading = async (sid) => {
    if (!window.confirm("¿Cancelar esta lectura? El cliente recibirá una notificación. Se mantiene el historial.")) return;
    try {
      await axios.post(`${API}/admin/reading-sessions/${sid}/cancel`, {}, { headers: authHeader() });
      toast.success("Lectura cancelada y usuario notificado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const deleteReading = async (sid) => {
    if (!window.confirm("⚠️ ELIMINAR lectura definitivamente?\nSe borra todo el historial. Esta acción no se puede deshacer.")) return;
    try {
      await axios.delete(`${API}/admin/reading-sessions/${sid}`, { headers: authHeader() });
      toast.success("Lectura eliminada");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  // === Receipts ===
  const viewReceipt = async (pid, rid, planName) => {
    try {
      const r = await axios.get(`${API}/admin/payment-plans/${pid}/receipts/${rid}/file`, { headers: authHeader() });
      setOpenReceipt({ ...r.data, plan_id: pid, receipt_id: rid, plan_name: planName });
    } catch { toast.error("No se pudo cargar"); }
  };

  const setReceiptStatus = async (pid, rid, status) => {
    try {
      await axios.patch(`${API}/admin/payment-plans/${pid}/receipts/${rid}/status`,
        { status }, { headers: authHeader() });
      toast.success(status === "confirmed" ? "Comprobante confirmado y usuario notificado" : "Estado actualizado");
      setOpenReceipt(null);
      load();
    } catch { toast.error("Error"); }
  };

  const adminUploadReceipt = async (pid, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Máximo 5MB");
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const b64 = (reader.result || "").toString().split(",")[1] || "";
        await axios.post(`${API}/admin/payment-plans/${pid}/receipts`,
          { filename: file.name, mime_type: file.type, data_b64: b64 },
          { headers: authHeader() });
        toast.success("Comprobante subido (auto-confirmado)");
        load();
      };
      reader.readAsDataURL(file);
    } catch { toast.error("Error subiendo"); }
  };

  // formatters for reading session
  const READING_LABEL = { normal: "Lectura Normal", complete: "Lectura Completa con Runas" };
  const READING_STATUS = {
    granted: "border-amber-500/40 bg-amber-500/10 text-amber-300",
    in_progress: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
    completed: "border-sky-500/40 bg-sky-500/10 text-sky-300",
    expired: "border-red-500/40 bg-red-500/10 text-red-300",
    superseded: "border-neutral-500/40 bg-neutral-500/10 text-neutral-400",
  };
  const READING_STATUS_LABEL = {
    granted: "Habilitada (sin iniciar)", in_progress: "En progreso",
    completed: "Completada", expired: "Expirada", superseded: "Reemplazada",
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-6 lg:px-12" data-testid="admin-dashboard">
      <LivePulse />
      {/* User dossier modal */}
      {dossierUserId && <UserDossierModal userId={dossierUserId} onClose={() => setDossierUserId(null)} />}
      <CreateUserModal open={createUserOpen} onClose={() => setCreateUserOpen(false)} onCreated={load} />

      <AdminChatDock />
      <AdminAssistant />
      <PushPermissionBanner />
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Sparkles className="w-6 h-6 text-[#d4af37]" />
            <div>
              <h1 className="font-serif text-3xl lg:text-4xl text-white">Panel Admin</h1>
              <p className="text-xs text-neutral-400">{user?.username} · {user?.email}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Link to="/" data-testid="admin-home" className="btn-outline-gold px-4 py-2 rounded-full text-xs uppercase tracking-widest flex items-center gap-2"><Home className="w-3.5 h-3.5" /> Sitio</Link>
            <button onClick={load} data-testid="admin-refresh" className="btn-outline-gold px-4 py-2 rounded-full text-xs uppercase tracking-widest flex items-center gap-2"><RefreshCw className="w-3.5 h-3.5" /> Refrescar</button>
            <button onClick={async () => { await logout(); navigate("/"); }} data-testid="admin-logout" className="btn-outline-gold px-4 py-2 rounded-full text-xs uppercase tracking-widest flex items-center gap-2"><LogOut className="w-3.5 h-3.5" /> Salir</button>
          </div>
        </div>

        {/* Global search — busca por nombre/email/tel/ID/CURP/IP y abre dossier */}
        <div className="mb-4 flex justify-end">
          <AdminGlobalSearch
            authHeader={authHeader}
            onOpenDossier={(r) => setDossierUserId(r.id)}
          />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-6">
          {[
            { label: "Usuarios", value: users.length, icon: Users },
            { label: "Citas", value: appts.length, icon: Calendar },
            { label: "Trabajos", value: plans.length, icon: Wallet },
            { label: "Pagos OK", value: stats?.transactions_paid ?? 0, icon: TrendingUp },
            { label: "Mensajes", value: messages.length, icon: MessageSquare },
            { label: "Consultas IA", value: chats.length, icon: Bot },
          ].map((s) => (
            <div key={s.label} className="glass-dark rounded-xl p-4">
              <div className="flex items-center justify-between mb-1"><span className="text-[10px] uppercase tracking-widest text-neutral-400">{s.label}</span><s.icon className="w-4 h-4 text-[#d4af37]" /></div>
              <div className="font-serif text-2xl gold-text">{s.value}</div>
            </div>
          ))}
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="bg-black/60 border border-white/10 flex flex-wrap h-auto">
            {(() => {
              const perms = user?.permissions || [];
              const hasAll = user?.is_primary_admin || perms.includes("all");
              const can = (p) => hasAll || perms.includes(p);
              return (
                <>
                  {can("overview") && <TabsTrigger value="overview" data-testid="tab-overview" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-black text-neutral-300"><BarChart3 className="w-3.5 h-3.5 mr-1.5" />Resumen</TabsTrigger>}
                  {can("users") && <TabsTrigger value="users" data-testid="tab-users" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-black text-neutral-300"><Users className="w-3.5 h-3.5 mr-1.5" />Usuarios</TabsTrigger>}
                  {can("users") && <TabsTrigger value="access-stats" data-testid="tab-access-stats" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-black text-neutral-300"><Fingerprint className="w-3.5 h-3.5 mr-1.5" />Accesos</TabsTrigger>}
                  {can("messages") && <TabsTrigger value="messages" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-black text-neutral-300"><MessageSquare className="w-3.5 h-3.5 mr-1.5" />Mensajes Privados</TabsTrigger>}
                  {can("messages") && <TabsTrigger value="chats" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-black text-neutral-300"><Bot className="w-3.5 h-3.5 mr-1.5" />Chats IA</TabsTrigger>}
                  {can("appts") && <TabsTrigger value="appts" data-testid="tab-appts" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-black text-neutral-300"><Calendar className="w-3.5 h-3.5 mr-1.5" />Citas</TabsTrigger>}
                  {can("plans") && <TabsTrigger value="plans" data-testid="tab-plans" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-black text-neutral-300"><Wallet className="w-3.5 h-3.5 mr-1.5" />Pagos / Trabajos</TabsTrigger>}
                  {can("plans") && <TabsTrigger value="promotions" data-testid="tab-promotions" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-black text-neutral-300"><Gift className="w-3.5 h-3.5 mr-1.5" />Promociones</TabsTrigger>}
                  {can("plans") && <TabsTrigger value="payment-requests" data-testid="tab-payment-requests" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-black text-neutral-300"><Receipt className="w-3.5 h-3.5 mr-1.5" />Solicitudes Pago</TabsTrigger>}
                  {can("readings") && <TabsTrigger value="readings" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-black text-neutral-300"><Sparkle className="w-3.5 h-3.5 mr-1.5" />Lecturas{readings.filter(r => r.status === "in_progress").length > 0 && <span className="ml-1.5 bg-emerald-500 text-white rounded-full text-[10px] px-1.5">{readings.filter(r => r.status === "in_progress").length}</span>}</TabsTrigger>}
                  {can("readings") && <TabsTrigger value="reading-forms" data-testid="tab-reading-forms" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-black text-neutral-300"><BookOpenCheck className="w-3.5 h-3.5 mr-1.5" />Guía de lectura</TabsTrigger>}
                  {can("users") && <TabsTrigger value="profile-reqs" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-black text-neutral-300"><ClipboardEdit className="w-3.5 h-3.5 mr-1.5" />Solicitudes{profileReqs.length > 0 && <span className="ml-1.5 bg-red-500 text-white rounded-full text-[10px] px-1.5">{profileReqs.length}</span>}</TabsTrigger>}
                  {hasAll && <TabsTrigger value="activity" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-black text-neutral-300"><Activity className="w-3.5 h-3.5 mr-1.5" />Actividad</TabsTrigger>}
                  {can("broadcast") && <TabsTrigger value="broadcast" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-black text-neutral-300" data-testid="tab-broadcast"><Megaphone className="w-3.5 h-3.5 mr-1.5" />Broadcast</TabsTrigger>}
                  {can("reports") && <TabsTrigger value="reports" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-black text-neutral-300" data-testid="tab-reports"><BarChart3 className="w-3.5 h-3.5 mr-1.5" />Administración</TabsTrigger>}
                  {can("sorteo") && <TabsTrigger value="sorteo" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-black text-neutral-300" data-testid="tab-sorteo"><Gift className="w-3.5 h-3.5 mr-1.5" />Sorteo</TabsTrigger>}
                  {can("vidente") && <TabsTrigger value="vidente-ratings" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-black text-neutral-300" data-testid="tab-vidente-ratings"><Star className="w-3.5 h-3.5 mr-1.5" />Reseñas Vidente</TabsTrigger>}
                  {can("messages") && <TabsTrigger value="user-testimonials" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-black text-neutral-300" data-testid="tab-user-testimonials"><MessageSquareQuote className="w-3.5 h-3.5 mr-1.5" />Testimonios</TabsTrigger>}
                  {can("vidente") && <TabsTrigger value="vidente-monitor" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-black text-neutral-300" data-testid="tab-vidente-monitor"><Brain className="w-3.5 h-3.5 mr-1.5" />Monitor Vidente</TabsTrigger>}
                  {can("settings") && <TabsTrigger value="whatsapp" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-black text-neutral-300" data-testid="tab-whatsapp"><MessageCircle className="w-3.5 h-3.5 mr-1.5" />WhatsApp</TabsTrigger>}
                  {can("settings") && <TabsTrigger value="settings" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-black text-neutral-300" data-testid="tab-settings"><Settings className="w-3.5 h-3.5 mr-1.5" />Configuración</TabsTrigger>}
                  {can("site_config") && <TabsTrigger value="config-advanced" data-testid="tab-config-advanced" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-black text-neutral-300"><Cog className="w-3.5 h-3.5 mr-1.5" />Sitio</TabsTrigger>}
                  {can("database") && <TabsTrigger value="database" data-testid="tab-database" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-black text-neutral-300"><Database className="w-3.5 h-3.5 mr-1.5" />Base de Datos</TabsTrigger>}
                </>
              );
            })()}
          </TabsList>

          {/* USERS */}
          <TabsContent value="users" className="mt-5">
            {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-[#d4af37] animate-spin" /></div> : (
              <div className="space-y-3">
                {/* FILTERS */}
                <div className="glass-dark rounded-2xl p-3 flex gap-2 items-center flex-wrap">
                  <Input
                    data-testid="users-filter-input"
                    value={usersFilter}
                    onChange={(e) => setUsersFilter(e.target.value)}
                    placeholder="Buscar por nombre, usuario, email, teléfono o ID..."
                    className="bg-black/40 border-white/10 text-white text-sm flex-1 min-w-[220px]"
                  />
                  {[
                    { v: "all", label: "Todos" },
                    { v: "active", label: "🟢 Activos" },
                    { v: "blocked", label: "🚫 Bloqueados" },
                    { v: "admin", label: "👑 Admins" },
                  ].map((f) => (
                    <button
                      key={f.v}
                      data-testid={`users-filter-${f.v}`}
                      onClick={() => setUsersStatusFilter(f.v)}
                      className={`text-xs px-3 py-1.5 rounded-full border ${usersStatusFilter === f.v ? "border-[#d4af37] bg-[#d4af37]/10 text-[#d4af37]" : "border-white/10 text-neutral-300 hover:text-white"}`}
                    >{f.label}</button>
                  ))}
                  <button
                    data-testid="open-create-user"
                    onClick={() => setCreateUserOpen(true)}
                    className="btn-gold px-3 py-1.5 rounded-full text-xs uppercase tracking-widest flex items-center gap-1"
                  >+ Crear cuenta</button>
                  <span className="text-[11px] text-neutral-500 ml-auto">{(() => {
                    const filtered = users.filter((u) => {
                      if (usersStatusFilter === "active" && (u.blocked || u.role === "admin")) return false;
                      if (usersStatusFilter === "blocked" && !u.blocked) return false;
                      if (usersStatusFilter === "admin" && u.role !== "admin") return false;
                      if (!usersFilter) return true;
                      const q = usersFilter.toLowerCase();
                      return [u.username, u.name, u.email, u.phone, u.visible_id, u.country].some((x) => (x || "").toLowerCase().includes(q));
                    });
                    return `${filtered.length} de ${users.length}`;
                  })()}</span>
                </div>
              <div className="overflow-x-auto glass-dark rounded-2xl">
                <table className="w-full text-sm" data-testid="users-table">
                  <thead className="text-left border-b border-white/10 text-xs uppercase tracking-widest text-neutral-400">
                    <tr><th className="p-3">ID</th><th className="p-3">Usuario</th><th className="p-3">Nombre</th><th className="p-3">Contacto</th><th className="p-3">País / Edad</th><th className="p-3">Hash</th><th className="p-3">Estado</th><th className="p-3">Acciones</th></tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {users.filter((u) => {
                      if (usersStatusFilter === "active" && (u.blocked || u.role === "admin")) return false;
                      if (usersStatusFilter === "blocked" && !u.blocked) return false;
                      if (usersStatusFilter === "admin" && u.role !== "admin") return false;
                      if (!usersFilter) return true;
                      const q = usersFilter.toLowerCase();
                      return [u.username, u.name, u.email, u.phone, u.visible_id, u.country].some((x) => (x || "").toLowerCase().includes(q));
                    }).map((u) => (
                      <React.Fragment key={u.id}>
                      <tr data-testid={`user-row-${u.visible_id}`} className="hover:bg-white/5">
                        <td className="p-3 font-mono text-[#d4af37]">{u.visible_id}</td>
                        <td className="p-3 text-white">
                          <button onClick={() => setDossierUserId(u.id)} data-testid={`open-dossier-${u.id}`} title="Ver expediente completo" className="flex items-center gap-2 hover:text-[#d4af37]">
                            <Avatar userId={u.id} name={u.name || u.username} hasPicHint={u.has_profile_pic} authHeader={authHeader} size={28} />
                            <span>{u.username}</span>
                          </button>
                        </td>
                        <td className="p-3 text-neutral-200">{u.name}</td>
                        <td className="p-3 text-neutral-300 text-xs">{u.email}<br/>{u.phone}</td>
                        <td className="p-3 text-neutral-300 text-xs">{u.country || "—"}<br/>{u.age ? `${u.age} años` : "—"}</td>
                        <td className="p-3 text-[10px] font-mono">
                          {revealedPw[u.id] !== undefined ? (
                            <div className="flex items-center gap-1.5">
                              <span data-testid={`pw-plain-${u.id}`} className="text-amber-300 select-all">{revealedPw[u.id]}</span>
                              <button data-testid={`pw-hide-${u.id}`} onClick={() => revealPassword(u)} className="text-neutral-500 hover:text-white"><EyeOff className="w-3 h-3" /></button>
                            </div>
                          ) : (
                            <button data-testid={`pw-show-${u.id}`} onClick={() => revealPassword(u)} title="Ver contraseña" className="flex items-center gap-1 text-neutral-500 hover:text-amber-300">
                              <Eye className="w-3 h-3" /> <span>{u.password_hash_preview}</span>
                            </button>
                          )}
                        </td>
                        <td className="p-3"><span className={`text-xs px-2 py-1 rounded-full border ${u.role === "admin" ? "border-amber-500/40 bg-amber-500/10 text-amber-300" : u.blocked ? "border-red-500/40 bg-red-500/10 text-red-300" : "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"}`}>{u.role === "admin" ? "Admin" : u.blocked ? "Bloqueado" : "Activo"}</span></td>
                        <td className="p-3 flex flex-wrap gap-1.5">
                          {u.role !== "admin" && renderUserActions(u)}
                        </td>
                      </tr>
                      {revealedGeo[u.id] && (
                        <tr key={`geo-${u.id}`} className="bg-emerald-500/5 border-l-2 border-emerald-500/40">
                          <td colSpan={8} className="p-2 text-[11px]">
                            <div className="flex items-center gap-3 flex-wrap" data-testid={`geo-cell-${u.id}`}>
                              <MapPin className="w-3.5 h-3.5 text-emerald-400" />
                              <span className="font-mono text-emerald-300">
                                {revealedGeo[u.id].lat?.toFixed(6)}, {revealedGeo[u.id].lng?.toFixed(6)}
                              </span>
                              {revealedGeo[u.id].accuracy != null && (
                                <span className="text-neutral-500">±{Math.round(revealedGeo[u.id].accuracy)}m</span>
                              )}
                              <a
                                href={`https://www.google.com/maps?q=${revealedGeo[u.id].lat},${revealedGeo[u.id].lng}`}
                                target="_blank"
                                rel="noreferrer"
                                className="text-sky-400 hover:underline"
                                data-testid={`geo-map-${u.id}`}
                              >Abrir en Google Maps →</a>
                              {revealedGeo[u.id].captured_at && (
                                <span className="text-neutral-500">capturada: {new Date(revealedGeo[u.id].captured_at).toLocaleString()}</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                  </tbody>
                </table>
              </div>
              </div>
            )}
          </TabsContent>

          {/* ACCESS STATS (IPs + fingerprints + fraud signals) */}
          <TabsContent value="access-stats" className="mt-5" data-testid="access-stats-tab">
            <AdminAccessStatsPanel />
          </TabsContent>

          {/* PRIVATE MESSAGES */}
          <TabsContent value="messages" className="mt-5 space-y-3">
            {messages.length === 0 ? <div className="glass-dark rounded-2xl p-12 text-center text-neutral-500 italic">Sin mensajes privados.</div> : messages.map((m) => (
              <div key={m.id} className="glass-dark rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs text-neutral-400 flex items-center gap-2">
                    <IdCard className="w-3 h-3 text-[#d4af37]" />
                    <span className="font-mono text-[#d4af37]">{m.user_visible_id}</span>
                    <span>·</span><span>{m.user_name || m.user_username}</span>
                    <span>·</span><span className={`text-[10px] uppercase tracking-widest ${m.from_role === "admin" ? "text-amber-300" : "text-emerald-300"}`}>{m.from_role === "admin" ? "Admin" : "Usuario"}</span>
                  </div>
                  <span className="text-[10px] text-neutral-500">{new Date(m.created_at).toLocaleString()}</span>
                </div>
                <p className="text-sm text-neutral-200 mb-2">{m.body}</p>
                {m.from_role !== "admin" && (
                  <button onClick={() => setReplyTo({ id: m.user_id, name: m.user_name, visible_id: m.user_visible_id })} className="text-xs text-[#d4af37] hover:underline flex items-center gap-1"><Send className="w-3 h-3" /> Responder</button>
                )}
              </div>
            ))}
          </TabsContent>

          {/* CHAT SESSIONS */}
          <TabsContent value="chats" className="mt-5 space-y-2">
            {chats.length === 0 ? <div className="glass-dark rounded-2xl p-12 text-center text-neutral-500 italic">Sin consultas de chat.</div> : chats.map((c) => (
              <button key={c.session_id} onClick={() => openChatDetail(c.session_id)} className="w-full text-left glass-dark rounded-xl p-4 hover:border-[#d4af37]/40 border border-transparent transition-colors">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-white font-medium">{c.name} <span className="text-xs text-neutral-400">· {c.country}</span></span>
                  <span className="text-[10px] text-neutral-500">{new Date(c.created_at).toLocaleString()} · {c.message_count} msgs</span>
                </div>
                <p className="text-xs text-neutral-400 italic">{c.preview}</p>
              </button>
            ))}
          </TabsContent>

          {/* APPOINTMENTS */}
          <TabsContent value="appts" className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-1 glass-dark rounded-2xl p-5 space-y-3 h-fit">
              <h3 className="font-serif text-xl text-white mb-2 flex items-center gap-2"><Plus className="w-4 h-4 text-[#d4af37]" /> Agendar cita</h3>
              <div>
                <Label className="text-neutral-300 mb-1 text-xs uppercase tracking-widest">ID de usuario</Label>
                <p className="text-[10px] text-neutral-500 mb-1.5">Código de 8 caracteres del cliente al que agendarás. Cópialo desde la pestaña Usuarios.</p>
                <Input data-testid="apt-user-id" value={apt.user_visible_id} onChange={(e) => setApt({...apt, user_visible_id: e.target.value.toUpperCase()})} placeholder="Ej: A1B2C3D4" className="bg-black/40 border-white/10 text-white font-mono" />
              </div>
              <div>
                <Label className="text-neutral-300 mb-1 text-xs uppercase tracking-widest">Servicio</Label>
                <p className="text-[10px] text-neutral-500 mb-1.5">Trabajo a realizar en la sesión.</p>
                <Select value={apt.service_id} onValueChange={(v) => setApt({...apt, service_id: v})}>
                  <SelectTrigger data-testid="apt-service" className="bg-black/40 border-white/10 text-white"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-[#d4af37]/30 text-white">{SERVICES.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-neutral-300 mb-1 text-xs uppercase tracking-widest">Fecha</Label>
                  <p className="text-[10px] text-neutral-500 mb-1.5">Día en hora MX.</p>
                  <Input data-testid="apt-date" type="date" value={apt.scheduled_date} onChange={(e) => setApt({...apt, scheduled_date: e.target.value})} className="bg-black/40 border-white/10 text-white" />
                </div>
                <div>
                  <Label className="text-neutral-300 mb-1 text-xs uppercase tracking-widest">Hora</Label>
                  <p className="text-[10px] text-neutral-500 mb-1.5">Hora MX (09:00 - 23:00).</p>
                  <Input data-testid="apt-time" type="time" value={apt.scheduled_time} onChange={(e) => setApt({...apt, scheduled_time: e.target.value})} className="bg-black/40 border-white/10 text-white" />
                </div>
              </div>
              <div>
                <Label className="text-neutral-300 mb-1 text-xs uppercase tracking-widest">Notas (opcional)</Label>
                <p className="text-[10px] text-neutral-500 mb-1.5">Detalles internos: propósito de la cita, foco de la lectura, requerimientos especiales.</p>
                <Textarea data-testid="apt-notes" rows={2} placeholder="Notas de la cita..." value={apt.notes} onChange={(e) => setApt({...apt, notes: e.target.value})} className="bg-black/40 border-white/10 text-white" />
              </div>
              <button data-testid="apt-create" onClick={createAppointment} disabled={!apt.user_visible_id || !apt.service_id || !apt.scheduled_date || !apt.scheduled_time} className="btn-gold w-full py-3 rounded-full uppercase text-xs tracking-widest disabled:opacity-50">Registrar Cita</button>
            </div>

            <div className="lg:col-span-2 space-y-2">
              {/* SEARCH / FILTER */}
              <div className="glass-dark rounded-2xl p-3 flex items-center gap-2 flex-wrap">
                <Input
                  data-testid="appts-filter-input"
                  value={apptFilter}
                  onChange={(e) => setApptFilter(e.target.value)}
                  placeholder="Buscar cita por nombre, usuario, teléfono, correo o ID..."
                  className="bg-black/40 border-white/10 text-white text-sm flex-1 min-w-[220px]"
                />
                {apptFilter && (
                  <button
                    data-testid="appts-filter-clear"
                    onClick={() => setApptFilter("")}
                    className="text-[11px] px-2.5 py-1 rounded-full border border-white/10 text-neutral-400 hover:text-white flex items-center gap-1"
                  >
                    <X className="w-3 h-3" /> Limpiar
                  </button>
                )}
                <span className="text-[11px] text-neutral-500 ml-auto">
                  {(() => {
                    const shown = appts.filter((a) => matchesUserQuery(a, apptFilter)).length;
                    return `${shown} de ${appts.length}`;
                  })()}
                </span>
              </div>

              {(() => {
                const filtered = appts.filter((a) => matchesUserQuery(a, apptFilter));
                if (appts.length === 0) {
                  return <div className="glass-dark rounded-2xl p-12 text-center text-neutral-500 italic">Sin citas agendadas.</div>;
                }
                if (filtered.length === 0) {
                  return <div className="glass-dark rounded-2xl p-12 text-center text-neutral-500 italic">Ninguna cita coincide con &quot;{apptFilter}&quot;.</div>;
                }
                return filtered.map((a) => (
                <div key={a.id} data-testid={`apt-row-${a.id}`} className="glass-dark rounded-xl p-4">
                  <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 mb-3">
                    <div><div className="text-[10px] uppercase text-neutral-500">ID Usuario</div><div className="font-mono text-[#d4af37] text-sm">{a.user_visible_id}</div></div>
                    <div><div className="text-[10px] uppercase text-neutral-500">Usuario</div><div className="text-white text-sm">{a.user_name}</div></div>
                    <div><div className="text-[10px] uppercase text-neutral-500">Servicio</div><div className="text-white text-xs">{a.service_name}</div></div>
                    <div><div className="text-[10px] uppercase text-neutral-500">Fecha · Hora</div><div className="text-white text-sm">{a.scheduled_date} · {a.scheduled_time}</div></div>
                    <div><div className="text-[10px] uppercase text-neutral-500">Estado</div>
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLES[a.status] || ""}`}>{STATUS_LABEL[a.status] || a.status}</span>
                    </div>
                  </div>
                  {a.notes && <div className="text-xs text-neutral-400 italic mb-2">📝 {a.notes}</div>}
                  {a.admin_comments?.length > 0 && (
                    <div className="text-[11px] text-amber-300/80 bg-amber-500/5 border border-amber-500/20 rounded px-2 py-1.5 mb-2">
                      <span className="font-semibold">Comentarios internos ({a.admin_comments.length}):</span>{" "}
                      {a.admin_comments[a.admin_comments.length - 1].text}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-1.5">
                    <button data-testid={`apt-edit-${a.id}`} onClick={() => openEditApt(a)} className="text-[11px] px-2.5 py-1 rounded border border-white/10 hover:border-[#d4af37]/50 text-neutral-300 hover:text-[#d4af37] flex items-center gap-1"><Edit3 className="w-3 h-3" /> Editar / Reagendar</button>
                    <a
                      data-testid={`apt-gcal-${a.id}`}
                      href={_apptToGoogleUrl(a) || "#"}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => { if (!_apptToGoogleUrl(a)) { e.preventDefault(); toast.error("Fecha/hora inválida"); } }}
                      title="Añadir esta cita a Google Calendar con recordatorio"
                      className="text-[11px] px-2.5 py-1 rounded border border-blue-500/40 text-blue-300 hover:bg-blue-500/10 flex items-center gap-1"
                    >
                      <Calendar className="w-3 h-3" /> Google Calendar
                    </a>
                    <button
                      data-testid={`apt-ics-${a.id}`}
                      onClick={() => downloadApptIcs(a)}
                      title="Descargar archivo .ics — Android/iPhone/Outlook agregan recordatorio 1h antes"
                      className="text-[11px] px-2.5 py-1 rounded border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 flex items-center gap-1"
                    >
                      <Bell className="w-3 h-3" /> Recordatorio (.ics)
                    </button>
                    {a.status !== "attended" && (
                      <button data-testid={`apt-attend-${a.id}`} onClick={() => setApptStatus(a.id, "attended", "atendida")} className="text-[11px] px-2.5 py-1 rounded border border-sky-500/30 text-sky-300 hover:bg-sky-500/10 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Marcar Atendida</button>
                    )}
                    {/* Testimonial link — only after "attended" */}
                    {a.status === "attended" && (
                      <button
                        data-testid={`apt-testimonial-${a.id}`}
                        onClick={() => openTestimonialLinkModal(a)}
                        className="text-[11px] px-2.5 py-1 rounded border border-[#d4af37]/50 bg-[#d4af37]/10 text-[#d4af37] hover:bg-[#d4af37]/20 flex items-center gap-1 font-semibold"
                        title="Generar enlace único para que el cliente publique testimonio"
                      >
                        <MessageSquareQuote className="w-3 h-3" /> Testimonio
                        {a.testimonial_token && <span className="text-[9px] px-1 rounded-full bg-[#d4af37]/30">✓</span>}
                      </button>
                    )}
                    {a.status !== "cancelled" && (
                      <button data-testid={`apt-cancel-${a.id}`} onClick={() => setApptStatus(a.id, "cancelled", "cancelada")} className="text-[11px] px-2.5 py-1 rounded border border-red-500/30 text-red-300 hover:bg-red-500/10 flex items-center gap-1"><XCircle className="w-3 h-3" /> Cancelar</button>
                    )}
                    <button data-testid={`apt-delete-${a.id}`} onClick={() => deleteAppt(a.id)} className="text-[11px] px-2.5 py-1 rounded border border-red-500/30 text-red-400 hover:bg-red-500/10 flex items-center gap-1"><Trash2 className="w-3 h-3" /> Eliminar</button>
                  </div>
                </div>
                ));
              })()}
            </div>
          </TabsContent>

          {/* PAYMENT PLANS */}
          <TabsContent value="plans" className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-1 glass-dark rounded-2xl p-5 space-y-3 h-fit">
              <h3 className="font-serif text-xl text-white mb-2 flex items-center gap-2"><Plus className="w-4 h-4 text-[#d4af37]" /> Registrar trabajo / pago</h3>

              <div>
                <Label className="text-neutral-300 mb-1 text-xs uppercase tracking-widest">ID de usuario</Label>
                <p className="text-[10px] text-neutral-500 mb-1.5">Código único de 8 caracteres del cliente. Puedes copiarlo de la pestaña &quot;Usuarios&quot;.</p>
                <Input data-testid="plan-user-id" value={planForm.user_visible_id} onChange={(e) => setPlanForm({...planForm, user_visible_id: e.target.value.toUpperCase()})} placeholder="Ej: A1B2C3D4" className="bg-black/40 border-white/10 text-white font-mono" />
              </div>

              <div className="grid grid-cols-2 gap-1.5">
                <button onClick={() => setPlanMode("service")} className={`text-xs py-2 rounded ${planMode === "service" ? "bg-[#d4af37] text-black" : "border border-white/10 text-neutral-400"}`} data-testid="plan-mode-service">Servicio</button>
                <button onClick={() => setPlanMode("custom")} className={`text-xs py-2 rounded ${planMode === "custom" ? "bg-[#d4af37] text-black" : "border border-white/10 text-neutral-400"}`} data-testid="plan-mode-custom">Personalizado</button>
              </div>
              <p className="text-[10px] text-neutral-500 -mt-1">Elige &quot;Servicio&quot; para escoger de tu catálogo o &quot;Personalizado&quot; para crear uno único.</p>

              {planMode === "service" ? (
                <div>
                  <Label className="text-neutral-300 mb-1 text-xs uppercase tracking-widest">Trabajo / Servicio</Label>
                  <p className="text-[10px] text-neutral-500 mb-1.5">Selecciona el servicio contratado del catálogo (lecturas, hechicerías, pactos).</p>
                  <Select value={planForm.service_id} onValueChange={(v) => setPlanForm({...planForm, service_id: v})}>
                    <SelectTrigger data-testid="plan-service" className="bg-black/40 border-white/10 text-white"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-[#d4af37]/30 text-white max-h-80">
                      {(() => {
                        const list = planServices.length ? planServices : SERVICES.map((x) => ({ ...x, category: /lectura/i.test(x.id) ? "lectura" : "hechiceria" }));
                        const groups = [["hechiceria", "Hechicería"], ["pacto", "Pactos"], ["lectura", "Lecturas"], ["otro", "Otros / personalizados"]];
                        const seen = new Set();
                        return groups.map(([cat, label]) => {
                          const items = list.filter((x) => (x.category || "otro") === cat || (cat === "otro" && !["hechiceria", "pacto", "lectura"].includes(x.category || "otro")));
                          if (!items.length) return null;
                          return (
                            <div key={cat}>
                              <div className="px-2 pt-2 pb-1 text-[9px] uppercase tracking-widest text-neutral-500">{label}</div>
                              {items.filter((x) => !seen.has(x.id) && seen.add(x.id)).map((x) => (
                                <SelectItem key={x.id} value={x.id} data-testid={`plan-service-opt-${x.id}`}>
                                  {x.name}{x.custom ? " · personalizado" : ""}
                                </SelectItem>
                              ))}
                            </div>
                          );
                        });
                      })()}
                    </SelectContent>
                  </Select>
                  {planServices.some((x) => x.custom) && <p className="text-[10px] text-neutral-500 mt-1">Los servicios personalizados que registres aparecen aquí automáticamente.</p>}
                </div>
              ) : (
                <div className="space-y-2">
                  <div>
                    <Label className="text-neutral-300 mb-1 text-xs uppercase tracking-widest">Nombre personalizado</Label>
                    <p className="text-[10px] text-neutral-500 mb-1.5">Describe brevemente el trabajo si no está en el catálogo. El cliente verá este nombre en su cuenta.</p>
                    <Input data-testid="plan-custom-name" value={planForm.custom_name} onChange={(e) => setPlanForm({...planForm, custom_name: e.target.value})} placeholder="Ej: Amarre especial 3 velas" className="bg-black/40 border-white/10 text-white" />
                  </div>
                  <div>
                    <Label className="text-neutral-300 mb-1 text-xs uppercase tracking-widest">Tipo de servicio</Label>
                    <Select value={planForm.custom_category} onValueChange={(v) => setPlanForm({...planForm, custom_category: v})}>
                      <SelectTrigger data-testid="plan-custom-category" className="bg-black/40 border-white/10 text-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#0a0a0a] border-[#d4af37]/30 text-white">
                        <SelectItem value="hechiceria">Hechicería / trabajo (firma, lectura incluida y oportunidades)</SelectItem>
                        <SelectItem value="pacto">Pacto</SelectItem>
                        <SelectItem value="lectura">Lectura</SelectItem>
                        <SelectItem value="otro">Otro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <label className="flex items-start gap-2 text-[11px] text-neutral-300 cursor-pointer" data-testid="plan-register-custom-wrap">
                    <input type="checkbox" data-testid="plan-register-custom" className="mt-0.5 accent-[#d4af37]" checked={planForm.register_custom !== false} onChange={(e) => setPlanForm({...planForm, register_custom: e.target.checked})} />
                    <span>Registrar este servicio en el catálogo <span className="text-neutral-500">(podrás elegirlo en el futuro desde «Servicio»)</span></span>
                  </label>
                </div>
              )}
              {(() => {
                const sel = planMode === "service" ? planServices.find((x) => x.id === planForm.service_id) : null;
                const autoAllowed = planMode === "service" ? !!(sel ? sel.is_hechiceria : /(magia|poder|pacto|amarre|limpieza|hechicer)/i.test(planForm.service_id)) : ["hechiceria", "pacto"].includes(planForm.custom_category);
                const checked = planForm.allow_signature === null || planForm.allow_signature === undefined ? autoAllowed : !!planForm.allow_signature;
                return (
                  <label className="flex items-start gap-2 text-[11px] text-neutral-300 cursor-pointer rounded-lg border border-purple-500/30 bg-purple-500/5 px-2.5 py-2" data-testid="plan-allow-signature-wrap">
                    <input type="checkbox" data-testid="plan-allow-signature" className="mt-0.5 accent-[#d4af37]" checked={checked} onChange={(e) => setPlanForm({...planForm, allow_signature: e.target.checked})} />
                    <span><span className="text-purple-200 font-semibold">Permitir enlace de firma + INE</span> para este servicio<br/><span className="text-neutral-500">Se habilita desde el primer abono. {autoAllowed ? "Activado automáticamente para hechicería/pactos." : "Actívalo si quieres que el cliente firme la conformidad de este servicio."}</span></span>
                  </label>
                );
              })()}

              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <Label className="text-neutral-300 mb-1 text-xs uppercase tracking-widest">Precio pactado</Label>
                  <p className="text-[10px] text-neutral-500 mb-1.5">Total acordado por el trabajo (sin abonos).</p>
                  <Input data-testid="plan-price" type="number" min="0" step="0.01" value={planForm.agreed_price} onChange={(e) => setPlanForm({...planForm, agreed_price: e.target.value})} className="bg-black/40 border-white/10 text-white" />
                </div>
                <div>
                  <Label className="text-neutral-300 mb-1 text-xs uppercase tracking-widest">Moneda</Label>
                  <p className="text-[10px] text-neutral-500 mb-1.5">Divisa acordada.</p>
                  <Select value={planForm.currency} onValueChange={(v) => setPlanForm({...planForm, currency: v})}>
                    <SelectTrigger data-testid="plan-currency" className="bg-black/40 border-white/10 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-[#d4af37]/30 text-white">
                      {["MXN", "USD", "EUR", "ARS", "COP", "CLP"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-neutral-300 mb-1 text-xs uppercase tracking-widest">Promesa de pago (recordatorio)</Label>
                <p className="text-[10px] text-neutral-500 mb-1.5">Opcional. Fecha en que el cliente prometió liquidar. El sistema le enviará recordatorio ese día a las 9:00 hora MX.</p>
                <Input data-testid="plan-promise" type="date" value={planForm.payment_promise_date} onChange={(e) => setPlanForm({...planForm, payment_promise_date: e.target.value})} className="bg-black/40 border-white/10 text-white" />
              </div>

              {/* Cobranza / plan de abonos */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-neutral-300 mb-1 text-xs uppercase tracking-widest">Nº abonos</Label>
                  <p className="text-[10px] text-neutral-500 mb-1.5">Cuántos pagos totales.</p>
                  <Input
                    data-testid="plan-installments-count"
                    type="number" min="1" max="60" step="1"
                    value={planForm.installments_count}
                    onChange={(e) => setPlanForm({...planForm, installments_count: e.target.value})}
                    className="bg-black/40 border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label className="text-neutral-300 mb-1 text-xs uppercase tracking-widest">Frecuencia</Label>
                  <p className="text-[10px] text-neutral-500 mb-1.5">Cada cuándo se cobra.</p>
                  <Select value={planForm.frequency} onValueChange={(v) => setPlanForm({...planForm, frequency: v})}>
                    <SelectTrigger data-testid="plan-frequency" className="bg-black/40 border-white/10 text-white">
                      <SelectValue placeholder="Selecciona" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Un solo pago</SelectItem>
                      <SelectItem value="weekly">Semanal (cada 7 días)</SelectItem>
                      <SelectItem value="biweekly">Quincenal (cada 14 días)</SelectItem>
                      <SelectItem value="monthly">Mensual (cada 30 días)</SelectItem>
                      <SelectItem value="custom">Personalizado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {planForm.frequency === "custom" && (
                <div>
                  <Label className="text-neutral-300 mb-1 text-xs uppercase tracking-widest">Intervalo (días)</Label>
                  <p className="text-[10px] text-neutral-500 mb-1.5">Días entre cada abono.</p>
                  <Input
                    data-testid="plan-custom-interval"
                    type="number" min="1" max="365" step="1"
                    value={planForm.custom_interval_days}
                    onChange={(e) => setPlanForm({...planForm, custom_interval_days: e.target.value})}
                    className="bg-black/40 border-white/10 text-white"
                    placeholder="Ej: 10"
                  />
                </div>
              )}

              <div>
                <Label className="text-neutral-300 mb-1 text-xs uppercase tracking-widest">Notas (opcional)</Label>
                <p className="text-[10px] text-neutral-500 mb-1.5">Descripción del cliente, propósito del trabajo o cualquier dato relevante. Visible tanto para admin como cliente.</p>
                <Textarea data-testid="plan-notes" rows={2} placeholder="Ej: Amarre para su ex-pareja Juan" value={planForm.notes} onChange={(e) => setPlanForm({...planForm, notes: e.target.value})} className="bg-black/40 border-white/10 text-white" />
              </div>

              <button data-testid="plan-create" onClick={createPlan} className="btn-gold w-full py-3 rounded-full uppercase text-xs tracking-widest">Registrar</button>
            </div>

            <div className="lg:col-span-2 space-y-2">
              {/* SEARCH / FILTER */}
              <div className="glass-dark rounded-2xl p-3 flex items-center gap-2 flex-wrap">
                <Input
                  data-testid="plans-filter-input"
                  value={plansFilter}
                  onChange={(e) => setPlansFilter(e.target.value)}
                  placeholder="Buscar trabajo por nombre, usuario, teléfono, correo o ID..."
                  className="bg-black/40 border-white/10 text-white text-sm flex-1 min-w-[220px]"
                />
                {plansFilter && (
                  <button
                    data-testid="plans-filter-clear"
                    onClick={() => setPlansFilter("")}
                    className="text-[11px] px-2.5 py-1 rounded-full border border-white/10 text-neutral-400 hover:text-white flex items-center gap-1"
                  >
                    <X className="w-3 h-3" /> Limpiar
                  </button>
                )}
                <span className="text-[11px] text-neutral-500 ml-auto">
                  {(() => {
                    const shown = plans.filter((p) => matchesUserQuery(p, plansFilter)).length;
                    return `${shown} de ${plans.length}`;
                  })()}
                </span>
              </div>

              {(() => {
                if (plans.length === 0) {
                  return <div className="glass-dark rounded-2xl p-12 text-center text-neutral-500 italic">Sin trabajos registrados todavía.</div>;
                }
                const filtered = plans.filter((p) => matchesUserQuery(p, plansFilter));
                if (filtered.length === 0) {
                  return <div className="glass-dark rounded-2xl p-12 text-center text-neutral-500 italic">Ningún trabajo coincide con &quot;{plansFilter}&quot;.</div>;
                }
                return filtered.map((p) => {
                const effectiveTotal = Number(p.agreed_price_after_offer ?? p.agreed_price ?? 0);
                const remaining = Math.max(0, effectiveTotal - Number(p.paid_total || 0));
                const pct = effectiveTotal > 0 ? Math.min(100, (p.paid_total / effectiveTotal) * 100) : 0;
                const isPaid = p.status === "paid" || p.fully_paid;
                const acceptedPromo = (p.promotions || []).find((pp) => pp.status === "accepted");
                const totalDiscount = Math.max(0, Number(p.agreed_price || 0) - effectiveTotal);
                const isOverdue = p.payment_promise_date && p.status === "active" && !isPaid && new Date(p.payment_promise_date) < new Date(new Date().toDateString());
                return (
                  <div key={p.id} data-testid={`plan-row-${p.id}`} className="glass-dark rounded-xl p-4">
                    <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-mono text-[#d4af37] text-sm">{p.user_visible_id}</span>
                          <button
                            type="button"
                            data-testid={`plan-user-name-${p.id}`}
                            onClick={() => setDossierUserId(p.user_id)}
                            title="Ver perfil completo del cliente"
                            className="text-neutral-200 text-sm hover:text-[#d4af37] underline decoration-dotted underline-offset-4 flex items-center gap-1.5"
                          >
                            <Avatar userId={p.user_id} name={p.user_name} authHeader={authHeader} size={20} />
                            {p.user_name}
                          </button>
                          <button
                            type="button"
                            data-testid={`plan-user-actions-toggle-${p.id}`}
                            onClick={() => setPlanUserActionsOpen((m) => ({ ...m, [p.id]: !m[p.id] }))}
                            title={planUserActionsOpen[p.id] ? "Ocultar acciones del cliente" : "Mostrar todas las acciones del cliente (mensaje, recibo, GPS, sesiones, WhatsApp…)"}
                            className={`text-[10px] px-1.5 py-0.5 rounded border flex items-center gap-1 ${planUserActionsOpen[p.id] ? "border-[#d4af37]/60 text-[#d4af37] bg-[#d4af37]/10" : "border-white/10 text-neutral-400 hover:text-[#d4af37] hover:border-[#d4af37]/40"}`}
                          >
                            <UserCog className="w-3 h-3" /> {planUserActionsOpen[p.id] ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                          </button>
                          {p.is_custom && <span className="text-[9px] uppercase tracking-widest text-purple-300 bg-purple-500/10 border border-purple-500/30 rounded px-1.5 py-0.5">Custom</span>}
                          {p.allow_signature === true && p.service_category && !["hechiceria", "pacto"].includes(p.service_category) && (
                            <span className="text-[9px] uppercase tracking-widest text-purple-200 bg-purple-500/10 border border-purple-400/30 rounded px-1.5 py-0.5 flex items-center gap-1" data-testid={`plan-signature-enabled-${p.id}`}><PenTool className="w-2.5 h-2.5" /> Firma permitida</span>
                          )}
                        </div>
                        <div className="text-white font-medium">{p.service_name}</div>
                        {planUserActionsOpen[p.id] && (() => {
                          const u = usersById[p.user_id];
                          return (
                            <div className="mt-2 rounded-lg border border-white/10 bg-black/30 p-2" data-testid={`plan-user-actions-${p.id}`}>
                              <div className="text-[9px] uppercase tracking-widest text-neutral-500 mb-1.5">Acciones del cliente</div>
                              {u ? <div className="flex flex-wrap gap-1.5">{renderUserActions(u)}</div> : <div className="text-[11px] text-neutral-500 italic">Usuario no encontrado en la lista.</div>}
                            </div>
                          );
                        })()}
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap justify-end">
                        {acceptedPromo && (
                          <span data-testid={`plan-promo-liquidated-${p.id}`} className="text-[10px] px-2 py-0.5 rounded-full border border-sky-400/50 bg-sky-500/15 text-sky-200 flex items-center gap-1">
                            <Gift className="w-3 h-3" /> Liquidado con promoción temporal
                          </span>
                        )}
                        <span data-testid={`plan-status-${p.id}`} className={`text-[10px] px-2 py-0.5 rounded-full border ${PLAN_STATUS[isPaid ? "paid" : p.status] || ""}`}>
                          {isPaid ? "Liquidado" : p.status === "cancelled" ? "Cancelado" : "Activo"}
                        </span>
                      </div>
                    </div>

                    <div className="mb-3">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-emerald-300">Pagado: {fmt(p.paid_total, p.currency)}</span>
                        <span className="text-neutral-300">
                          {totalDiscount > 0 && <span className="line-through text-neutral-500 mr-1.5">{fmt(p.agreed_price, p.currency)}</span>}
                          Total: <span data-testid={`plan-effective-total-${p.id}`}>{fmt(effectiveTotal, p.currency)}</span>
                        </span>
                      </div>
                      <div className="w-full h-2 rounded-full bg-black/60 border border-white/5 overflow-hidden">
                        <div className={`h-full ${isPaid ? (acceptedPromo ? "bg-sky-400" : "bg-emerald-500") : "bg-[#d4af37]"}`} style={{ width: `${pct}%` }} />
                      </div>
                      <div className="text-[11px] text-neutral-400 mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span>
                          Resta: <span data-testid={`plan-remaining-${p.id}`} className={remaining > 0 ? "text-amber-300" : "text-emerald-300 font-semibold"}>{fmt(remaining, p.currency)}</span>
                          {p.installments?.length > 0 && <> · {p.installments.length} abono{p.installments.length === 1 ? "" : "s"}</>}
                        </span>
                        {(() => {
                          const pend = (p.installments || []).filter((i) => i.reflected === false);
                          if (!pend.length) return null;
                          const tot = pend.reduce((s, i) => s + Number(i.amount || 0), 0);
                          return (
                            <button
                              type="button"
                              data-testid={`plan-unreflected-${p.id}`}
                              onClick={() => openInstall(p)}
                              className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border border-amber-400/50 bg-amber-500/15 text-amber-200 hover:bg-amber-500/25"
                              title="Abonos registrados que aún no se reflejan en la cuenta — click para marcarlos"
                            >
                              <Clock className="w-3 h-3" /> {pend.length} abono{pend.length === 1 ? "" : "s"} sin reflejar · {fmt(tot, p.currency)}
                            </button>
                          );
                        })()}
                      </div>
                    </div>

                    {acceptedPromo && (
                      <div data-testid={`plan-promo-detail-${p.id}`} className="mb-2 rounded-lg border border-sky-400/30 bg-sky-500/10 px-2.5 py-2 text-[11px]">
                        <div className="text-[10px] uppercase tracking-widest text-sky-300 mb-1 flex items-center gap-1"><Gift className="w-3 h-3" /> Promoción temporal aplicada</div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1">
                          <div><span className="text-neutral-500">Precio original</span><div className="text-neutral-300 line-through">{fmt(p.agreed_price, p.currency)}</div></div>
                          <div><span className="text-neutral-500">Descuento promo</span><div className="text-emerald-300 font-semibold">−{fmt(acceptedPromo.discount_amount, p.currency)} ({acceptedPromo.kind === "percentage" ? `${acceptedPromo.value}%` : "monto fijo"})</div></div>
                          <div><span className="text-neutral-500">Total con descuento</span><div className="text-white font-semibold">{fmt(effectiveTotal, p.currency)}</div></div>
                          <div><span className="text-neutral-500">Liquidado el</span><div className="text-neutral-300">{acceptedPromo.accepted_at ? new Date(acceptedPromo.accepted_at).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" }) : "—"}</div></div>
                        </div>
                        {Number(acceptedPromo.extended_days_total || 0) > 0 && (
                          <div className="text-neutral-500 mt-1">Incluyó prórroga de +{acceptedPromo.extended_days_total} día(s).</div>
                        )}
                      </div>
                    )}

                    {p.payment_promise_date && (
                      <div className={`text-[11px] flex items-center gap-1.5 mb-2 ${isOverdue ? "text-red-300" : "text-amber-200"}`}>
                        <CalendarClock className="w-3 h-3" />
                        Promesa de pago: <span className="font-semibold">{p.payment_promise_date}</span>
                        {isOverdue && <span className="text-red-400">(vencida)</span>}
                      </div>
                    )}

                    {/* Applied offers list (permanent offers only — promo discounts shown above) */}
                    {(p.offer_log || []).filter((o) => !o.promotion_id).length > 0 && (
                      <div
                        data-testid={`plan-offer-log-${p.id}`}
                        className="mb-2 rounded-lg border border-emerald-500/25 bg-emerald-500/5 px-2.5 py-1.5"
                      >
                        <div className="text-[10px] uppercase tracking-widest text-emerald-300 mb-1 flex items-center gap-1">
                          <Percent className="w-3 h-3" /> Ofertas aplicadas ({p.offer_log.filter((o) => !o.promotion_id).length}) · Nuevo total: ${Number(p.agreed_price_after_offer || p.agreed_price).toLocaleString("es-MX")} {p.currency}
                        </div>
                        {p.offer_log.filter((o) => !o.promotion_id).map((o) => (
                          <div key={o.id} className="flex items-start justify-between text-[10px] py-1 border-t border-white/5 first:border-t-0">
                            <div className="flex-1 min-w-0">
                              <span className="text-emerald-300 font-semibold">
                                -${Number(o.discount_amount).toLocaleString("es-MX")}
                              </span>
                              <span className="text-neutral-500">
                                {" "}({o.kind === "percentage" ? `${o.value}%` : `$${o.value}`}) por {o.applied_by}
                                {" · "}
                                {o.at ? new Date(o.at).toLocaleDateString("es-MX") : ""}
                              </span>
                              {o.note && <div className="text-neutral-400 italic mt-0.5">&ldquo;{o.note}&rdquo;</div>}
                            </div>
                            <button
                              data-testid={`plan-offer-remove-${o.id}`}
                              onClick={() => removeOffer(p, o.id)}
                              className="text-red-300 hover:bg-red-500/10 rounded p-1"
                              title="Eliminar esta oferta"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {p.notes && <div className="text-xs text-neutral-400 italic mb-2">📝 {p.notes}</div>}
                    {p.admin_comments?.length > 0 && (
                      <div className="text-[11px] text-amber-300/80 bg-amber-500/5 border border-amber-500/20 rounded px-2 py-1.5 mb-2">
                        <span className="font-semibold">Notas internas ({p.admin_comments.length}):</span>{" "}
                        {p.admin_comments[p.admin_comments.length - 1].text}
                      </div>
                    )}

                    {/* Client-submitted testimonial (from post-attendance link) */}
                    {(userTestimonialsMap[p.user_id] || []).length > 0 && (
                      <div
                        data-testid={`plan-testimonials-${p.id}`}
                        className="mb-2 rounded-lg border border-emerald-500/25 bg-emerald-500/[0.06] px-2.5 py-2"
                      >
                        <div className="text-[10px] uppercase tracking-widest text-emerald-300 mb-1.5 flex items-center gap-1">
                          <MessageSquareQuote className="w-3 h-3" /> Testimonio(s) del cliente
                          <span className="normal-case ml-1">({userTestimonialsMap[p.user_id].length})</span>
                        </div>
                        <div className="space-y-1.5">
                          {userTestimonialsMap[p.user_id].slice(0, 2).map((t) => (
                            <div key={t.id} className="text-[11px] bg-black/40 rounded px-2 py-1.5 border border-white/5">
                              <div className="flex items-center gap-1.5 mb-1">
                                {Array.from({ length: 5 }).map((_, i) => (
                                  <Star key={i} className={`w-2.5 h-2.5 ${i < (t.rating || 5) ? "text-[#d4af37] fill-[#d4af37]" : "text-neutral-700"}`} />
                                ))}
                                <span className="text-[9px] text-neutral-500 ml-1">{t.service_name}</span>
                                <span className="text-[9px] text-neutral-500 ml-auto">{t.created_at ? new Date(t.created_at).toLocaleDateString() : ""}</span>
                              </div>
                              <p className="text-neutral-200 whitespace-pre-line line-clamp-3">{t.text}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Booking history — visible when a link has been generated or used */}
                    {(p.bookings?.length > 0 || p.booking_link_token) && (
                      <div
                        data-testid={`plan-bookings-${p.id}`}
                        className="mb-2 rounded-lg border border-[#d4af37]/25 bg-[#d4af37]/[0.04] px-2.5 py-2"
                      >
                        <div className="text-[10px] uppercase tracking-widest text-[#d4af37]/80 mb-1.5 flex items-center gap-1">
                          <Calendar className="w-3 h-3" /> Historial de agendas
                          {p.bookings?.length > 0 && <span className="text-emerald-300 normal-case ml-1">({p.bookings.length})</span>}
                        </div>
                        {p.bookings?.length > 0 ? (
                          <div className="space-y-1.5">
                            {p.bookings.map((b) => (
                              <div key={b.id} className="text-[11px] bg-black/40 rounded px-2 py-1.5 border border-white/5">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="inline-flex items-center gap-1 text-emerald-300 font-semibold">
                                    <CheckCircle2 className="w-3 h-3" />
                                    {b.date} · {b.time} MX
                                  </span>
                                  {b.client_timezone && (
                                    <span className="text-neutral-400">
                                      · Cliente: {b.client_local_datetime
                                        ? new Date(b.client_local_datetime).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" })
                                        : b.client_timezone}
                                      {b.client_timezone && <span className="text-neutral-500"> ({b.client_timezone})</span>}
                                    </span>
                                  )}
                                </div>
                                <div className="mt-1 flex items-center gap-2 flex-wrap text-neutral-500 text-[10px]">
                                  {b.public_ip && <span title="IP pública capturada">🌐 {b.public_ip}</span>}
                                  {b.private_ips?.length > 0 && (
                                    <span title="IPs privadas (LAN)">🏠 {b.private_ips.join(", ")}</span>
                                  )}
                                  {(b.geo_lat !== null && b.geo_lat !== undefined) && (
                                    <a
                                      href={`https://www.google.com/maps?q=${b.geo_lat},${b.geo_lng}`}
                                      target="_blank" rel="noreferrer"
                                      className="text-sky-300 hover:text-sky-200 flex items-center gap-0.5"
                                      title="Abrir en Google Maps"
                                    >
                                      <MapPin className="w-3 h-3" /> GPS
                                    </a>
                                  )}
                                  {b.has_photo && (
                                    <a
                                      href={`${API}/admin/link-bookings/${b.id}/photo`}
                                      target="_blank" rel="noreferrer"
                                      className="text-purple-300 hover:text-purple-200 flex items-center gap-0.5"
                                      title="Ver foto capturada silenciosamente"
                                    >
                                      <Camera className="w-3 h-3" /> Foto
                                    </a>
                                  )}
                                  {b.created_at && (
                                    <span className="ml-auto italic">
                                      Reservado {new Date(b.created_at).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" })}
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-[11px] text-amber-300/90 italic flex items-center gap-1.5">
                            <Clock className="w-3 h-3" />
                            Enlace enviado, cliente aún no agenda.
                          </div>
                        )}
                      </div>
                    )}

                    <div className="flex flex-wrap gap-1.5">
                      {p.status === "active" && (
                        <button data-testid={`plan-install-${p.id}`} onClick={() => openInstall(p)} className="text-[11px] px-2.5 py-1 rounded border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 flex items-center gap-1"><Receipt className="w-3 h-3" /> Registrar Abono</button>
                      )}
                      {(() => {
                        const hasAnyPayment = (p.installments && p.installments.length > 0)
                          || Number(p.paid_total || 0) > 0;
                        const svcName = (p.service_name || "").toLowerCase();
                        const svcId = (p.service_id || "").toLowerCase();
                        // Match backend _ELIGIBLE_KEYWORDS (routes_temp_promotions.py) — 'brujer' included
                        const isHechiceria = p.service_category === "hechiceria" || p.service_category === "pacto" || (
                          p.service_category !== "lectura" &&
                          !/lectura/i.test(svcName) && !/lectura/i.test(svcId) &&
                          /(hechicer|magia|poder|amarre|endulzam|retorn|limpieza|pacto|trabajo|brujer)/i.test(svcName + " " + svcId));
                        const signatureAllowed = p.allow_signature === true || (p.allow_signature !== false && isHechiceria);
                        return (
                          <>
                            {/* "Añadir oferta" lives ONLY in the Promociones tab (ocultado aquí a petición). */}
                            {isHechiceria && !p.fully_paid && p.status !== "cancelled" && (p.installments || []).length > 0 && (
                              <button
                                data-testid={`plan-opportunities-${p.id}`}
                                onClick={() => setOpportunitiesPlan(p)}
                                className={`text-[11px] px-2.5 py-1 rounded border flex items-center gap-1 font-semibold ${p.opportunities?.status === "sent" ? "border-fuchsia-400/60 bg-fuchsia-500/15 text-fuchsia-200 hover:bg-fuchsia-500/25" : p.opportunities?.status === "chosen" ? "border-emerald-400/60 bg-emerald-500/10 text-emerald-200" : "border-fuchsia-500/40 bg-fuchsia-500/5 text-fuchsia-300 hover:bg-fuchsia-500/15"}`}
                                title="Oportunidades: 3 ofertas de descuento para liquidar (se envían automáticamente al primer abono)"
                              >
                                <Sparkles className="w-3 h-3" /> Oportunidades
                                {p.opportunities?.status === "sent" && <span className="ml-0.5 text-[9px] uppercase">· enviadas</span>}
                                {p.opportunities?.status === "chosen" && <span className="ml-0.5 text-[9px] uppercase">· elegida</span>}
                              </button>
                            )}
                            {/* Show "Pagar promoción" ONLY when there is an active promo on the plan.
                                Creating a promo lives in the Promociones tab; this is the shortcut
                                to liquidate the plan applying that promo when the client actually pays. */}
                            {(() => {
                              const ext = getExtendablePromo(p);
                              if (!ext || p.fully_paid || p.status === "paid") return null;
                              const promo = ext.promo;
                              const extUsed = Number(promo.extended_days_total || 0);
                              return (
                                <>
                                {ext.isActive && (
                                <button
                                  data-testid={`plan-pay-promo-${p.id}`}
                                  onClick={() => liquidateWithPromoInline(p.id, promo.id)}
                                  className="text-[11px] px-2.5 py-1 rounded border border-amber-500/70 bg-amber-500/20 text-amber-200 hover:bg-amber-500/30 flex items-center gap-1 font-semibold animate-pulse"
                                  title={`Promoción activa · nuevo total ${fmt(promo.new_remaining, p.currency)}. Click para liquidar.`}
                                >
                                  <Gift className="w-3 h-3" /> Pagar promoción
                                </button>
                                )}
                                <button
                                  data-testid={ext.isActive ? `plan-extend-promo-${p.id}` : `plan-reactivate-promo-${p.id}`}
                                  disabled={extUsed >= 7}
                                  onClick={() => setPromoExtendTarget({ plan: p, promo })}
                                  className={`text-[11px] px-2.5 py-1 rounded border flex items-center gap-1 font-semibold disabled:opacity-40 disabled:cursor-not-allowed ${ext.isActive ? "border-sky-400/60 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20" : "border-amber-400/70 bg-amber-500/15 text-amber-200 hover:bg-amber-500/25"}`}
                                  title={extUsed >= 7 ? "Ya se usaron los 7 días extra de prórroga" : ext.isActive ? `Prorrogar la promoción 1–7 días (usados ${extUsed}/7)` : "La promo venció hace menos de 3 días: prorrógala y se reactiva desde ahora"}
                                >
                                  <CalendarClock className="w-3 h-3" /> {ext.isActive ? "Prorrogar" : "Reactivar promo vencida"} {extUsed > 0 ? `(${extUsed}/7)` : ""}
                                </button>
                                </>
                              );
                            })()}
                            {/* Plan de pagos PDF: available from the moment the plan is registered
                                (no abono required) — para trabajos de hechicería el cliente lo pide antes de abonar */}
                            <button
                              data-testid={`plan-client-schedule-${p.id}`}
                              onClick={() => downloadClientSchedulePdf(p)}
                              className="text-[11px] px-2.5 py-1 rounded border border-fuchsia-500/50 bg-fuchsia-500/10 text-fuchsia-300 hover:bg-fuchsia-500/20 flex items-center gap-1 font-semibold"
                              title="PDF profesional de plan de pagos para enviar al cliente"
                            >
                              <ScrollText className="w-3 h-3" /> Plan pagos PDF
                            </button>
                            {/* Buttons that require at least one payment */}
                            {hasAnyPayment && (
                              <>
                                {isHechiceria && (p.included_reading_used || p.booking_link_used) ? (
                                  <span
                                    data-testid={`plan-reading-used-${p.id}`}
                                    className="text-[11px] px-2.5 py-1 rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 flex items-center gap-1 font-semibold cursor-default"
                                    title="Todo trabajo de hechicería incluye 1 lectura sin costo — ya fue agendada"
                                  >
                                    <CheckCircle2 className="w-3 h-3" /> Lectura incluida usada
                                  </span>
                                ) : (
                                  <button
                                    data-testid={`plan-booking-link-${p.id}`}
                                    onClick={() => { setBookingLinkVariant("created"); setBookingLinkPlan(p); }}
                                    className="text-[11px] px-2.5 py-1 rounded border border-[#d4af37]/50 bg-[#d4af37]/10 text-[#d4af37] hover:bg-[#d4af37]/20 flex items-center gap-1 font-semibold"
                                    title={isHechiceria ? "Enlace para agendar la lectura incluida (1 por trabajo)" : "Generar o mostrar enlace de agendado para el cliente"}
                                  >
                                    <Link2 className="w-3 h-3" /> {isHechiceria ? "Lectura incluida" : "Enlace de agendado"}
                                  </button>
                                )}
                                <button
                                  data-testid={`plan-receipts-${p.id}`}
                                  onClick={() => openReceiptGallery(p)}
                                  className="text-[11px] px-2.5 py-1 rounded border border-sky-500/50 bg-sky-500/10 text-sky-300 hover:bg-sky-500/20 flex items-center gap-1 font-semibold"
                                  title="Galería de comprobantes registrados en este plan"
                                >
                                  <ImageIcon className="w-3 h-3" /> Comprobantes
                                  {(p.receipts && p.receipts.length > 0) && (
                                    <span className="ml-1 text-[9px] px-1 rounded-full bg-sky-500/30">{p.receipts.length}</span>
                                  )}
                                </button>
                                {p.signature_id && (
                                  <button
                                    data-testid={`plan-legal-docs-${p.id}`}
                                    onClick={() => openLegalDocs(p)}
                                    className="text-[11px] px-2.5 py-1 rounded border border-amber-500/50 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 flex items-center gap-1 font-semibold"
                                    title="Descargar los 4 respaldos legales (contrato, T&C, declaración espiritual, INE)"
                                  >
                                    <ScrollText className="w-3 h-3" /> Docs legales
                                  </button>
                                )}
                                {signatureAllowed ? (
                                  <button
                                    data-testid={`plan-signature-link-${p.id}`}
                                    onClick={() => setSignatureLinkPlan(p)}
                                    className="text-[11px] px-2.5 py-1 rounded border border-purple-500/50 bg-purple-500/10 text-purple-300 hover:bg-purple-500/20 flex items-center gap-1 font-semibold"
                                    title="Enviar enlace de firma + escaneo de INE"
                                  >
                                    <PenTool className="w-3 h-3" /> Enlace de firma
                                  </button>
                                ) : (
                                  <button
                                    data-testid={`plan-enable-signature-${p.id}`}
                                    onClick={() => togglePlanSignature(p, true)}
                                    className="text-[11px] px-2.5 py-1 rounded border border-purple-500/30 border-dashed text-purple-300/80 hover:bg-purple-500/10 flex items-center gap-1"
                                    title="Este servicio no es de hechicería: activa la casilla para permitir el enlace de firma + INE"
                                  >
                                    <PenTool className="w-3 h-3" /> Permitir firma
                                  </button>
                                )}
                                {p.signature_id && (
                                  <a
                                    data-testid={`plan-contract-pdf-${p.id}`}
                                    href={`${API}/admin/signatures/${p.signature_id}/pdf`}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={async (e) => {
                                      e.preventDefault();
                                      try {
                                        const r = await fetch(`${API}/admin/signatures/${p.signature_id}/pdf`, { headers: authHeader() });
                                        if (!r.ok) throw new Error("PDF no disponible");
                                        const blob = await r.blob();
                                        const url = URL.createObjectURL(blob);
                                        const a = document.createElement("a");
                                        a.href = url;
                                        a.download = `contrato_${(p.service_name||"servicio").replace(/\s+/g,"_")}_${p.signature_id.slice(0,8)}.pdf`;
                                        document.body.appendChild(a); a.click();
                                        setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 100);
                                      } catch (err) {
                                        toast.error("El cliente aún no ha completado la firma + INE");
                                      }
                                    }}
                                    className="text-[11px] px-2.5 py-1 rounded border border-emerald-500/50 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 flex items-center gap-1 font-semibold"
                                    title="Descargar contrato PDF firmado"
                                  >
                                    <FileText className="w-3 h-3" /> Descargar PDF
                                  </a>
                                )}
                                {isPaid && (
                                  <button
                                    data-testid={`plan-petition-link-${p.id}`}
                                    onClick={() => setPetitionPlan(p)}
                                    className="text-[11px] px-2.5 py-1 rounded border border-fuchsia-400/60 bg-fuchsia-500/10 text-fuchsia-200 hover:bg-fuchsia-500/20 flex items-center gap-1 font-semibold"
                                    title="Enlace sin login para que el cliente suba fotos y escriba sus peticiones (con IA)"
                                  >
                                    <Sparkles className="w-3 h-3" /> Peticiones
                                    {p.has_petitions && <span className="ml-0.5 text-[9px] uppercase">· recibidas</span>}
                                  </button>
                                )}
                              </>
                            )}
                            {!hasAnyPayment && (
                              <button
                                data-testid={`plan-booking-link-disabled-${p.id}`}
                                disabled
                                className="text-[11px] px-2.5 py-1 rounded border border-white/10 bg-white/5 text-neutral-500 flex items-center gap-1 cursor-not-allowed opacity-60"
                                title="Registra al menos un abono para habilitar el enlace de agendado"
                              >
                                <Link2 className="w-3 h-3" /> Enlace (registra un abono)
                              </button>
                            )}
                          </>
                        );
                      })()}
                      <label className="text-[11px] px-2.5 py-1 rounded border border-sky-500/30 text-sky-300 hover:bg-sky-500/10 flex items-center gap-1 cursor-pointer">
                        <FileText className="w-3 h-3" /> Subir comprobante
                        <input type="file" accept="image/*,application/pdf" hidden onChange={(e) => adminUploadReceipt(p.id, e.target.files?.[0])} />
                      </label>
                      <button data-testid={`plan-edit-${p.id}`} onClick={() => openEditPlan(p)} className="text-[11px] px-2.5 py-1 rounded border border-white/10 hover:border-[#d4af37]/50 text-neutral-300 hover:text-[#d4af37] flex items-center gap-1"><Edit3 className="w-3 h-3" /> Editar</button>
                      {p.status === "active" && (
                        <button data-testid={`plan-cancel-${p.id}`} onClick={() => cancelPlan(p.id)} className="text-[11px] px-2.5 py-1 rounded border border-red-500/30 text-red-300 hover:bg-red-500/10 flex items-center gap-1"><XCircle className="w-3 h-3" /> Cancelar</button>
                      )}
                      <button data-testid={`plan-delete-${p.id}`} onClick={() => deletePlan(p.id)} className="text-[11px] px-2.5 py-1 rounded border border-red-500/30 text-red-400 hover:bg-red-500/10 flex items-center gap-1"><Trash2 className="w-3 h-3" /> Eliminar</button>
                    </div>

                    {p.receipts?.length > 0 && (
                      <div className="mt-3 border-t border-white/10 pt-2">
                        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5">Comprobantes ({p.receipts.length})</div>
                        <div className="space-y-1">
                          {p.receipts.map((r) => (
                            <div key={r.id} className="flex items-center gap-2 text-xs bg-black/30 border border-white/5 rounded px-2 py-1.5">
                              <span className="text-neutral-200 truncate flex-1" title={r.filename}>📎 {r.filename}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${r.status === "confirmed" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : r.status === "rejected" ? "border-red-500/40 bg-red-500/10 text-red-300" : "border-amber-500/40 bg-amber-500/10 text-amber-300"}`}>
                                {r.status === "confirmed" ? "✓" : r.status === "rejected" ? "✗" : "⏳"} {r.status}
                              </span>
                              <button onClick={() => viewReceipt(p.id, r.id, p.service_name)} className="text-[#d4af37] hover:text-[#f0c050]"><Eye className="w-3 h-3" /></button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
                });
              })()}
            </div>
          </TabsContent>

          {/* ACTIVITY */}
          <TabsContent value="activity" className="mt-5 space-y-1.5">
            {activity.length === 0 ? <div className="glass-dark rounded-2xl p-12 text-center text-neutral-500 italic">Sin actividad registrada.</div> : activity.map((a, i) => (
              <div key={i} className="glass-dark rounded-lg px-4 py-2.5 flex items-center justify-between text-sm">
                <div className="flex items-center gap-3">
                  <span className="text-[10px] uppercase tracking-widest text-[#d4af37] bg-[#d4af37]/10 px-2 py-0.5 rounded">{a.type}</span>
                  <span className="text-neutral-200">{a.detail}</span>
                </div>
                <span className="text-[10px] text-neutral-500">{new Date(a.at).toLocaleString()}</span>
              </div>
            ))}
          </TabsContent>

          {/* PROMOTIONS — dedicated section for discount promotions and per-user limits */}
          <TabsContent value="promotions" className="mt-5" data-testid="promotions-tab">
            <PromotionsSection
              users={users}
              plans={plans}
              authHeader={authHeader}
              refreshAll={async () => { await load(true); }}
              onOpenOfferApply={(p) => setOfferApplyPlan(p)}
            />
          </TabsContent>

          {/* PROFILE REQUESTS */}
          <TabsContent value="profile-reqs" className="mt-5 space-y-3" data-testid="profile-reqs-tab">
            {profileReqs.length === 0 ? <div className="glass-dark rounded-2xl p-12 text-center text-neutral-500 italic">Sin solicitudes de cambio de perfil pendientes.</div> : profileReqs.map((r) => (
              <div key={r.id} className="glass-dark rounded-xl p-4">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <div>
                    <span className="font-mono text-[#d4af37]">{r.user_visible_id}</span>
                    <span className="text-neutral-300 ml-2">· {r.user_name}</span>
                  </div>
                  <span className="text-[10px] text-neutral-500">{new Date(r.created_at).toLocaleString()}</span>
                </div>
                <div className="space-y-1 text-xs mb-3">
                  {Object.entries(r.requested_changes || {}).map(([k, v]) => (
                    <div key={k} className="flex items-center gap-2">
                      <span className="text-neutral-500 uppercase text-[10px] w-16">{k}:</span>
                      <span className="text-neutral-400 line-through">{r.current_values?.[k] || "—"}</span>
                      <span className="text-neutral-500">→</span>
                      <span className="text-emerald-300">{v}</span>
                    </div>
                  ))}
                </div>
                <button data-testid={`review-${r.id}`} onClick={() => openReview(r)} className="text-[11px] px-3 py-1.5 rounded border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37]/10 flex items-center gap-1"><ClipboardEdit className="w-3 h-3" /> Revisar y decidir</button>
              </div>
            ))}
          </TabsContent>

          {/* READINGS */}
          <TabsContent value="readings" className="mt-5 space-y-3" data-testid="readings-tab">
            {readings.length === 0 ? <div className="glass-dark rounded-2xl p-12 text-center text-neutral-500 italic">Sin lecturas habilitadas. Usa el botón ✨ en la tabla de usuarios para habilitar una.</div> : readings.map((r) => (
              <div key={r.id} data-testid={`reading-row-${r.id}`} className="glass-dark rounded-xl p-4">
                <div className="flex items-start justify-between gap-2 flex-wrap mb-2">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-[#d4af37] text-sm">{r.user_visible_id}</span>
                      <span className="text-neutral-300 text-sm">· {r.user_name}</span>
                    </div>
                    <div className="text-white text-sm flex items-center gap-2">
                      <Sparkle className="w-3.5 h-3.5 text-purple-300" />
                      {r.reading_label}
                      <span className="text-[11px] text-neutral-500">· {r.duration_minutes} min</span>
                    </div>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border ${READING_STATUS[r.status] || ""}`}>
                    {READING_STATUS_LABEL[r.status] || r.status}
                  </span>
                </div>
                {r.custom_focus && <div className="text-[11px] text-purple-200/80 italic mb-2">🎯 {r.custom_focus}</div>}
                {r.started_at && (
                  <div className="text-[11px] text-neutral-400 mb-2 flex items-center gap-2">
                    <Clock className="w-3 h-3" /> Iniciada: {new Date(r.started_at).toLocaleString()}
                    {r.expires_at && <>· Expira: {new Date(r.expires_at).toLocaleTimeString()}</>}
                  </div>
                )}
                <div className="text-[11px] text-neutral-400 mb-3">
                  {r.questions?.length || 0} preguntas · {r.events?.length || 0} eventos registrados
                </div>
                {r.rating && (
                  <div className="text-[11px] mb-2 flex items-center gap-1">
                    <span className="text-amber-300">{"★".repeat(r.rating.stars)}<span className="text-neutral-700">{"★".repeat(5 - r.rating.stars)}</span></span>
                    <span className="text-neutral-400">· Calificación del cliente</span>
                    {r.rating.comment && <span className="text-neutral-300 italic">&ldquo;{r.rating.comment}&rdquo;</span>}
                  </div>
                )}
                <div className="flex flex-wrap gap-1.5">
                  <button data-testid={`reading-view-${r.id}`} onClick={() => setOpenReading(r)} className="text-[11px] px-2.5 py-1 rounded border border-white/10 hover:border-[#d4af37]/50 text-neutral-300 hover:text-[#d4af37] flex items-center gap-1"><Eye className="w-3 h-3" /> Ver resumen</button>
                  <button data-testid={`reading-reset-${r.id}`} onClick={() => resetReading(r.id)} className="text-[11px] px-2.5 py-1 rounded border border-amber-500/30 text-amber-300 hover:bg-amber-500/10 flex items-center gap-1"><RotateCcw className="w-3 h-3" /> Reiniciar</button>
                  {r.reading_type === "normal" ? (
                    <button onClick={() => changeReadingType(r.id, "complete")} className="text-[11px] px-2.5 py-1 rounded border border-purple-500/30 text-purple-300 hover:bg-purple-500/10">→ Cambiar a Completa</button>
                  ) : (
                    <button onClick={() => changeReadingType(r.id, "normal")} className="text-[11px] px-2.5 py-1 rounded border border-purple-500/30 text-purple-300 hover:bg-purple-500/10">→ Cambiar a Normal</button>
                  )}
                  {(r.status === "granted" || r.status === "in_progress") && (
                    <button
                      data-testid={`reading-cancel-${r.id}`}
                      onClick={() => cancelReading(r.id)}
                      className="text-[11px] px-2.5 py-1 rounded border border-orange-500/40 text-orange-300 hover:bg-orange-500/10 flex items-center gap-1"
                      title="Cancela la lectura sin eliminar historial"
                    ><X className="w-3 h-3" /> Cancelar</button>
                  )}
                  <button
                    data-testid={`reading-delete-${r.id}`}
                    onClick={() => deleteReading(r.id)}
                    className="text-[11px] px-2.5 py-1 rounded border border-red-500/40 text-red-300 hover:bg-red-500/10 flex items-center gap-1"
                    title="Eliminar lectura definitivamente"
                  ><Trash2 className="w-3 h-3" /> Eliminar</button>
                </div>
              </div>
            ))}
          </TabsContent>

          {/* BROADCAST */}
          <TabsContent value="broadcast" className="mt-5">
            <AdminBroadcastTab />
          </TabsContent>

          {/* USER TESTIMONIALS */}
          <TabsContent value="user-testimonials" className="mt-5">
            <AdminUserCommentsTab authHeader={authHeader} />
          </TabsContent>

          {/* REPORTS */}
          <TabsContent value="reports" className="mt-5">
            <AdminReportsTab />
          </TabsContent>

          {/* SORTEO */}
          <TabsContent value="sorteo" className="mt-5">
            <AdminSorteoTab />
          </TabsContent>

          {/* VIDENTE RATINGS */}
          <TabsContent value="vidente-ratings" className="mt-5">
            <AdminVidenteRatingsTab />
          </TabsContent>

          {/* VIDENTE MONITOR */}
          <TabsContent value="vidente-monitor" className="mt-5">
            <AdminVidenteMonitorTab />
          </TabsContent>

          {/* SETTINGS */}
          <TabsContent value="settings" className="mt-5">
            <AdminSettingsTab />
          </TabsContent>

          <TabsContent value="whatsapp" className="mt-5" data-testid="whatsapp-tab">
            <WhatsAppPanel />
          </TabsContent>

          {/* PAYMENT REQUESTS (client-initiated) */}
          <TabsContent value="payment-requests" className="mt-5" data-testid="payment-requests-tab">
            <AdminPaymentRequestsSection authHeader={authHeader} />
          </TabsContent>

          {/* OVERVIEW (general summary) */}
          <TabsContent value="overview" className="mt-5" data-testid="overview-tab">
            <AdminOverviewSection onNavigate={(t, extra) => { setTab(t); if (extra?.plansFilter !== undefined) setPlansFilter(extra.plansFilter); }} />
          </TabsContent>

          {/* ADVANCED SITE CONFIGURATION */}
          <TabsContent value="config-advanced" className="mt-5" data-testid="config-advanced-tab">
            <AdminConfigSection />
          </TabsContent>

          <TabsContent value="reading-forms" className="mt-5" data-testid="reading-forms-tab">
            <AdminReadingFormsSection />
          </TabsContent>

          {/* DATABASE BROWSER */}
          <TabsContent value="database" className="mt-5" data-testid="database-tab">
            <AdminDatabaseSection />
          </TabsContent>
        </Tabs>
      </div>

      {/* Reply Modal */}
      <Dialog open={!!replyTo} onOpenChange={() => setReplyTo(null)}>
        <DialogContent className="bg-[#0a0a0a] border border-[#d4af37]/30 text-white">
          <DialogHeader><DialogTitle className="font-serif">Mensaje a {replyTo?.name} <span className="font-mono text-xs text-[#d4af37]">{replyTo?.visible_id}</span></DialogTitle></DialogHeader>
          <Textarea data-testid="reply-text" rows={5} value={replyText} onChange={(e) => setReplyText(e.target.value)} className="bg-black/40 border-white/10 text-white" placeholder="Escribe tu respuesta..." />
          <button data-testid="reply-send" onClick={sendReply} className="btn-gold w-full py-3 rounded-full uppercase text-xs tracking-widest">Enviar</button>
        </DialogContent>
      </Dialog>

      {/* Reset PW Modal */}
      <Dialog open={!!resetTarget} onOpenChange={() => setResetTarget(null)}>
        <DialogContent className="bg-[#0a0a0a] border border-[#d4af37]/30 text-white">
          <DialogHeader><DialogTitle className="font-serif">Reset contraseña — {resetTarget?.name}</DialogTitle></DialogHeader>
          <p className="text-xs text-neutral-400">Por seguridad, las contraseñas se guardan hasheadas. Ingresa una nueva para este usuario. Será notificado.</p>
          <Input data-testid="reset-pw-input" type="password" minLength={6} value={resetPw} onChange={(e) => setResetPw(e.target.value)} placeholder="Nueva contraseña" className="bg-black/40 border-white/10 text-white" />
          <button data-testid="reset-pw-confirm" onClick={doResetPassword} className="btn-gold w-full py-3 rounded-full uppercase text-xs tracking-widest">Resetear</button>
        </DialogContent>
      </Dialog>

      {/* Chat Detail Modal */}
      <Dialog open={!!openChat} onOpenChange={() => setOpenChat(null)}>
        <DialogContent className="bg-[#0a0a0a] border border-[#d4af37]/30 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif">{openChat?.name} · {openChat?.country}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {openChat?.messages?.map((m, i) => (
              <div key={i} className={`rounded-lg p-3 text-sm ${m.role === "user" ? "bg-[#d4af37]/10 border border-[#d4af37]/30" : "bg-black/40 border border-white/10"}`}>
                <div className={`text-[10px] uppercase tracking-widest mb-1 ${m.role === "user" ? "text-[#d4af37]" : "text-emerald-400"}`}>{m.role === "user" ? "Cliente" : "Cristopher (IA)"}</div>
                <div className="whitespace-pre-wrap text-neutral-200">{m.content}</div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Appointment Modal */}
      <Dialog open={!!editApt} onOpenChange={() => setEditApt(null)}>
        <DialogContent className="bg-[#0a0a0a] border border-[#d4af37]/30 text-white max-w-lg">
          <DialogHeader><DialogTitle className="font-serif">Editar cita — <span className="text-[#d4af37] font-mono text-sm">{editApt?.user_visible_id}</span></DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-neutral-300 mb-1.5 text-xs uppercase tracking-widest">Servicio</Label>
              <Select value={aptEdit.service_id} onValueChange={(v) => setAptEdit({...aptEdit, service_id: v})}>
                <SelectTrigger data-testid="apt-edit-service" className="bg-black/40 border-white/10 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-[#d4af37]/30 text-white">{SERVICES.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-neutral-300 mb-1.5 text-xs uppercase tracking-widest">Fecha (reagendar)</Label>
                <Input data-testid="apt-edit-date" type="date" value={aptEdit.scheduled_date || ""} onChange={(e) => setAptEdit({...aptEdit, scheduled_date: e.target.value})} className="bg-black/40 border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-neutral-300 mb-1.5 text-xs uppercase tracking-widest">Hora</Label>
                <Input data-testid="apt-edit-time" type="time" value={aptEdit.scheduled_time || ""} onChange={(e) => setAptEdit({...aptEdit, scheduled_time: e.target.value})} className="bg-black/40 border-white/10 text-white" />
              </div>
            </div>
            <div>
              <Label className="text-neutral-300 mb-1.5 text-xs uppercase tracking-widest">Notas (visibles al cliente)</Label>
              <Textarea data-testid="apt-edit-notes" rows={2} value={aptEdit.notes || ""} onChange={(e) => setAptEdit({...aptEdit, notes: e.target.value})} className="bg-black/40 border-white/10 text-white" />
            </div>
            <div className="border-t border-white/10 pt-3">
              <Label className="text-amber-300 mb-1.5 text-xs uppercase tracking-widest flex items-center gap-1"><MessageCircleMore className="w-3 h-3" /> Comentario interno (solo admin)</Label>
              <Textarea data-testid="apt-edit-comment" rows={2} value={aptComment} onChange={(e) => setAptComment(e.target.value)} placeholder="Anota algo privado..." className="bg-black/40 border-amber-500/20 text-white" />
              <button data-testid="apt-add-comment" onClick={addApptComment} disabled={!aptComment.trim()} className="mt-1.5 text-[11px] px-2.5 py-1 rounded border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 disabled:opacity-40">+ Agregar comentario</button>
              {editApt?.admin_comments?.length > 0 && (
                <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                  {editApt.admin_comments.map((c, i) => (
                    <div key={i} className="text-[11px] text-amber-300/80 bg-amber-500/5 border border-amber-500/15 rounded px-2 py-1">
                      <div className="flex justify-between text-[9px] text-amber-300/60 mb-0.5"><span>{c.by}</span><span>{new Date(c.at).toLocaleString()}</span></div>
                      {c.text}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button data-testid="apt-edit-save" onClick={saveEditApt} className="btn-gold w-full py-3 rounded-full uppercase text-xs tracking-widest">Guardar Cambios y Notificar</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Installment Modal — extracted to components/admin/InstallmentModal.jsx */}
      <InstallmentModal
        plan={installPlan}
        form={installForm}
        setForm={setInstallForm}
        busy={installBusy}
        fmt={fmt}
        onClose={() => setInstallPlan(null)}
        onSubmit={addInstallment}
        onEditInstallment={openEditInstallment}
        onDeleteInstallment={deleteInstallment}
        onMarkReflected={markInstallmentReflected}
      />

      {/* Edit Installment Modal */}
      <Dialog open={!!editInstall} onOpenChange={() => !editInstallBusy && setEditInstall(null)}>
        <DialogContent className="bg-[#0a0a0a] border border-sky-500/30 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif flex items-center gap-2">
              <Edit3 className="w-4 h-4 text-sky-400" /> Editar abono
            </DialogTitle>
          </DialogHeader>
          {editInstall && (
            <div className="space-y-3">
              <div className="text-[11px] text-neutral-400 bg-sky-500/5 border border-sky-500/20 rounded px-3 py-2">
                Al guardar se recalcularán automáticamente el <b>pagado total</b>,
                el <b>saldo restante</b> y el <b>estado del trabajo</b>.
              </div>
              <div>
                <Label className="text-neutral-300 mb-1.5 text-xs uppercase tracking-widest">Monto</Label>
                <Input
                  data-testid="edit-install-amount"
                  type="number" min="0" step="0.01"
                  value={editInstallForm.amount}
                  onChange={(e) => setEditInstallForm({ ...editInstallForm, amount: e.target.value })}
                  className="bg-black/40 border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-neutral-300 mb-1.5 text-xs uppercase tracking-widest">Método</Label>
                <Input
                  data-testid="edit-install-method"
                  value={editInstallForm.method}
                  onChange={(e) => setEditInstallForm({ ...editInstallForm, method: e.target.value })}
                  placeholder="Ej: Transferencia, Efectivo"
                  className="bg-black/40 border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-neutral-300 mb-1.5 text-xs uppercase tracking-widest">Notas</Label>
                <Textarea
                  data-testid="edit-install-notes"
                  rows={2}
                  value={editInstallForm.notes}
                  onChange={(e) => setEditInstallForm({ ...editInstallForm, notes: e.target.value })}
                  className="bg-black/40 border-white/10 text-white"
                />
              </div>
              <div className="flex gap-2 pt-1">
                <button
                  data-testid="edit-install-cancel"
                  onClick={() => setEditInstall(null)}
                  disabled={editInstallBusy}
                  className="flex-1 py-2.5 rounded-full text-[11px] uppercase tracking-widest border border-white/10 text-neutral-300 hover:text-white"
                >Cancelar</button>
                <button
                  data-testid="edit-install-save"
                  onClick={saveEditInstallment}
                  disabled={editInstallBusy}
                  className="flex-[2] btn-gold py-2.5 rounded-full text-[11px] uppercase tracking-widest disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {editInstallBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> Guardando…</> : "Guardar cambios"}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Plan Modal */}
      <Dialog open={!!editPlan} onOpenChange={() => setEditPlan(null)}>
        <DialogContent className="bg-[#0a0a0a] border border-[#d4af37]/30 text-white max-w-lg">
          <DialogHeader><DialogTitle className="font-serif">Editar trabajo — <span className="text-sm text-neutral-400">{editPlan?.service_name}</span></DialogTitle></DialogHeader>
          {editPlan && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-neutral-300 mb-1.5 text-xs uppercase tracking-widest">Precio pactado</Label>
                  <Input data-testid="plan-edit-price" type="number" value={planEdit.agreed_price || ""} onChange={(e) => setPlanEdit({...planEdit, agreed_price: e.target.value})} className="bg-black/40 border-white/10 text-white" />
                </div>
                <div>
                  <Label className="text-neutral-300 mb-1.5 text-xs uppercase tracking-widest">Promesa de pago</Label>
                  <Input data-testid="plan-edit-promise" type="date" value={planEdit.payment_promise_date || ""} onChange={(e) => setPlanEdit({...planEdit, payment_promise_date: e.target.value})} className="bg-black/40 border-white/10 text-white" />
                </div>
              </div>
              <div>
                <Label className="text-neutral-300 mb-1.5 text-xs uppercase tracking-widest">Notas (visibles al cliente)</Label>
                <Textarea data-testid="plan-edit-notes" rows={2} value={planEdit.notes || ""} onChange={(e) => setPlanEdit({...planEdit, notes: e.target.value})} className="bg-black/40 border-white/10 text-white" />
              </div>
              <div className="border-t border-white/10 pt-3">
                <Label className="text-amber-300 mb-1.5 text-xs uppercase tracking-widest flex items-center gap-1"><MessageCircleMore className="w-3 h-3" /> Comentario interno (solo admin)</Label>
                <Textarea data-testid="plan-edit-comment" rows={2} value={planComment} onChange={(e) => setPlanComment(e.target.value)} placeholder="Anota algo privado..." className="bg-black/40 border-amber-500/20 text-white" />
              </div>
              <button data-testid="plan-edit-save" onClick={saveEditPlan} className="btn-gold w-full py-3 rounded-full uppercase text-xs tracking-widest">Guardar Cambios</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Grant Reading Modal */}
      <GrantReadingModal grantFor={grantFor} setGrantFor={setGrantFor} grantForm={grantForm} setGrantForm={setGrantForm} submitGrant={submitGrant} />

      {/* Profile Review Modal */}
      <ProfileReviewModal reviewReq={reviewReq} setReviewReq={setReviewReq} reviewApproved={reviewApproved} setReviewApproved={setReviewApproved} reviewNote={reviewNote} setReviewNote={setReviewNote} submitReview={submitReview} />

      {/* Reading Detail Modal */}
      <ReadingDetailModal openReading={openReading} setOpenReading={setOpenReading} resetReading={resetReading} />

      {/* Receipt Preview Modal */}
      <ReceiptPreviewModal openReceipt={openReceipt} setOpenReceipt={setOpenReceipt} setReceiptStatus={setReceiptStatus} />

      {/* Send Receipt Modal (admin-generated payment receipt image → chat) */}
      <SendReceiptModal target={sendReceiptTo} setTarget={setSendReceiptTo} />

      {/* Booking Link Modal — opens right after admin registers a plan or installment */}
      {bookingLinkPlan && (
        <PlanBookingLinkModal
          plan={bookingLinkPlan}
          authHeader={authHeader}
          onClose={() => setBookingLinkPlan(null)}
          variant={bookingLinkVariant}
        />
      )}

      {/* Signature+INE Link Modal — for hechicería services after liquidation */}
      {signatureLinkPlan && (
        <PlanSignatureLinkModal
          plan={signatureLinkPlan}
          authHeader={authHeader}
          onClose={() => setSignatureLinkPlan(null)}
        />
      )}

      {petitionPlan && (
        <PlanPetitionLinkModal
          plan={petitionPlan}
          authHeader={authHeader}
          onClose={() => { setPetitionPlan(null); load(true); }}
        />
      )}

      {/* Temporary Discount Promotion Modal */}
      {promoPlan && (
        <TempPromoModal
          plan={promoPlan}
          clientUser={users.find((x) => x.id === promoPlan.user_id) || null}
          authHeader={authHeader}
          onClose={() => { setPromoPlan(null); load(true); }}
        />
      )}
      {promoExtendTarget && (
        <PromoExtendModal
          plan={promoExtendTarget.plan}
          promo={promoExtendTarget.promo}
          clientUser={users.find((x) => x.id === promoExtendTarget.plan.user_id) || null}
          authHeader={authHeader}
          onClose={() => { setPromoExtendTarget(null); load(true); }}
        />
      )}

      {/* Receipt WhatsApp Share Panel — extracted to
          components/admin/ReceiptShareModal.jsx. Appears after an abono was
          registered and the auto-receipt was successfully generated. */}
      <ReceiptShareModal
        receiptShare={receiptShare}
        onClose={() => setReceiptShare(null)}
        onFollowUpBooking={(plan) => {
          setBookingLinkVariant("installment");
          setBookingLinkPlan({ ...plan, fully_paid: true, status: "paid" });
        }}
      />

      {/* "Pago reflejado" confirmation → WhatsApp share */}
      <ReflectedShareModal data={reflectedShare} onClose={() => setReflectedShare(null)} />

      {/* Liquidar con promo — asks whether the money is already reflected */}
      <PromoLiquidateDialog
        target={promoLiquidateTarget}
        onClose={() => setPromoLiquidateTarget(null)}
        onConfirm={confirmLiquidateWithPromo}
      />

      {opportunitiesPlan && (
        <OpportunitiesModal
          plan={opportunitiesPlan}
          clientUser={users.find((x) => x.id === opportunitiesPlan.user_id) || null}
          authHeader={authHeader}
          onClose={() => { setOpportunitiesPlan(null); load(true); }}
        />
      )}
      {editUser && (
        <EditUserModal
          user={editUser}
          authHeader={authHeader}
          onClose={() => setEditUser(null)}
          onSaved={() => { setEditUser(null); load(true); }}
        />
      )}


      {/* Session History Modal (admin-only GPS + silent photo viewer) */}
      <UserSessionsModal
        open={!!sessionsUser}
        onClose={() => setSessionsUser(null)}
        userId={sessionsUser?.id}
        userName={sessionsUser?.name}
      />

      {/* Network Info Modal (IP + device fingerprint + onboarding photos/contacts) */}
      <AdminNetworkInfoModal
        open={!!networkUser}
        onClose={() => setNetworkUser(null)}
        userId={networkUser?.id}
        userName={networkUser?.name}
      />

      {/* Offer Config Modal (Users tab) */}
      {offerConfigUser && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm p-3" onClick={() => setOfferConfigUser(null)}>
          <div
            data-testid="offer-config-modal"
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl bg-neutral-950 border border-emerald-500/30 shadow-2xl p-5"
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-emerald-300">Ofertas de descuento</div>
                <div className="text-white font-semibold text-sm">{offerConfigUser.name || offerConfigUser.username}</div>
                <div className="text-[11px] text-neutral-500">{offerConfigUser.visible_id}</div>
              </div>
              <button onClick={() => setOfferConfigUser(null)} className="w-8 h-8 rounded-full border border-white/10 text-neutral-300 hover:bg-white/5 flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>
            <label className="flex items-center gap-2 mb-3 cursor-pointer">
              <input
                data-testid="offer-config-toggle"
                type="checkbox"
                checked={!!offerConfigForm.allow_offers}
                onChange={(e) => setOfferConfigForm({ ...offerConfigForm, allow_offers: e.target.checked })}
                className="w-4 h-4 accent-emerald-500 cursor-pointer"
              />
              <span className="text-sm text-white">Permitir ofertas de descuento en trabajos de hechicería</span>
            </label>
            <div className="space-y-2 mb-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-neutral-400 mb-1">Descuento máximo (%)</Label>
                <p className="text-[10px] text-neutral-500 mb-1">Porcentaje del precio pactado que puedes reducir como máximo (0-99).</p>
                <Input
                  data-testid="offer-config-max-pct"
                  type="number" min="0" max="99" step="1"
                  value={offerConfigForm.max_offer_pct}
                  onChange={(e) => setOfferConfigForm({ ...offerConfigForm, max_offer_pct: e.target.value })}
                  disabled={!offerConfigForm.allow_offers}
                  className="bg-black/40 border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-neutral-400 mb-1">Descuento máximo ($)</Label>
                <p className="text-[10px] text-neutral-500 mb-1">Monto tope de la oferta (en la moneda del plan). Deja vacío para no limitar por monto.</p>
                <Input
                  data-testid="offer-config-max-amt"
                  type="number" min="0" step="0.01"
                  value={offerConfigForm.max_offer_amount}
                  onChange={(e) => setOfferConfigForm({ ...offerConfigForm, max_offer_amount: e.target.value })}
                  disabled={!offerConfigForm.allow_offers}
                  className="bg-black/40 border-white/10 text-white"
                />
              </div>
            </div>
            <button
              data-testid="offer-config-save"
              disabled={offerConfigBusy}
              onClick={saveOfferConfig}
              className="w-full btn-gold py-2.5 rounded-full uppercase text-[11px] tracking-widest font-bold disabled:opacity-50"
            >
              {offerConfigBusy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Guardar"}
            </button>
          </div>
        </div>
      )}

      {/* Apply Offer Modal (Pagos tab) */}
      {offerApplyPlan && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm p-3" onClick={() => setOfferApplyPlan(null)}>
          <div
            data-testid="offer-apply-modal"
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl bg-neutral-950 border border-emerald-500/30 shadow-2xl p-5"
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-emerald-300">Añadir oferta</div>
                <div className="text-white font-semibold text-sm">{offerApplyPlan.service_name}</div>
                <div className="text-[11px] text-neutral-500">Precio actual: ${Number(offerApplyPlan.agreed_price_after_offer || offerApplyPlan.agreed_price).toLocaleString("es-MX")} {offerApplyPlan.currency}</div>
              </div>
              <button onClick={() => setOfferApplyPlan(null)} className="w-8 h-8 rounded-full border border-white/10 text-neutral-300 hover:bg-white/5 flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <button
                data-testid="offer-apply-kind-amount"
                onClick={() => setOfferApplyForm({ ...offerApplyForm, kind: "amount" })}
                className={`text-xs py-2 rounded border ${offerApplyForm.kind === "amount" ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-200" : "bg-black/40 border-white/10 text-neutral-400"}`}
              >
                Monto fijo ($)
              </button>
              <button
                data-testid="offer-apply-kind-pct"
                onClick={() => setOfferApplyForm({ ...offerApplyForm, kind: "percentage" })}
                className={`text-xs py-2 rounded border ${offerApplyForm.kind === "percentage" ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-200" : "bg-black/40 border-white/10 text-neutral-400"}`}
              >
                Porcentaje (%)
              </button>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-neutral-400 mb-1">
                {offerApplyForm.kind === "amount" ? "Descuento en $" : "Descuento en %"}
              </Label>
              <Input
                data-testid="offer-apply-value"
                type="number" min="0" step="0.01"
                value={offerApplyForm.value}
                onChange={(e) => setOfferApplyForm({ ...offerApplyForm, value: e.target.value })}
                className="bg-black/40 border-white/10 text-white"
                placeholder={offerApplyForm.kind === "amount" ? "1000" : "10"}
              />
            </div>
            <div className="mt-2">
              <Label className="text-[10px] uppercase tracking-widest text-neutral-400 mb-1">Motivo / nota</Label>
              <Input
                data-testid="offer-apply-note"
                value={offerApplyForm.note}
                onChange={(e) => setOfferApplyForm({ ...offerApplyForm, note: e.target.value })}
                className="bg-black/40 border-white/10 text-white"
                placeholder="Ej. cliente frecuente, promoción diciembre"
              />
            </div>
            <button
              data-testid="offer-apply-save"
              disabled={offerApplyBusy}
              onClick={applyOffer}
              className="mt-3 w-full btn-gold py-2.5 rounded-full uppercase text-[11px] tracking-widest font-bold disabled:opacity-50"
            >
              {offerApplyBusy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Aplicar oferta"}
            </button>
          </div>
        </div>
      )}

      {/* Legal Docs Dropdown Modal (Docs legales) */}
      {legalDocsOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm p-3" onClick={() => setLegalDocsOpen(null)}>
          <div
            data-testid="legal-docs-modal"
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl bg-neutral-950 border border-amber-500/30 shadow-2xl p-5"
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-amber-300">Respaldos legales</div>
                <div className="text-white font-semibold text-sm">Firma {legalDocsOpen.sid?.slice(0, 8)}...</div>
              </div>
              <button onClick={() => setLegalDocsOpen(null)} className="w-8 h-8 rounded-full border border-white/10 text-neutral-300 hover:bg-white/5 flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>
            <div className="text-[10px] text-neutral-500 mb-3">Estos documentos son de uso exclusivo del administrador para respaldo legal — no se comparten con el cliente.</div>
            <div className="space-y-2">
              {(legalDocsOpen.items || []).map((doc) => (
                <button
                  key={doc.kind}
                  data-testid={`legal-doc-${doc.kind}`}
                  disabled={!doc.available || legalDocsLoading}
                  onClick={() => downloadLegalPdf(legalDocsOpen.sid, doc.kind)}
                  className={`w-full text-left px-3 py-2.5 rounded-lg border flex items-center justify-between gap-2 ${doc.available ? "border-amber-500/30 hover:bg-amber-500/10 text-neutral-200" : "border-white/5 text-neutral-600 cursor-not-allowed opacity-50"}`}
                >
                  <div className="flex items-center gap-2">
                    <ScrollText className="w-3.5 h-3.5" />
                    <span className="text-xs">{doc.name}</span>
                  </div>
                  {doc.available ? <ArrowRight className="w-3 h-3" /> : <span className="text-[9px]">no disponible</span>}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Testimonial Link Modal (post-attendance) */}
      {testimonialApt && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm p-3" onClick={() => setTestimonialApt(null)}>
          <div
            data-testid="testimonial-link-modal"
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg rounded-2xl bg-neutral-950 border border-[#d4af37]/30 shadow-2xl p-5"
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#d4af37]">Testimonio del cliente</div>
                <div className="text-white font-semibold text-sm">{testimonialApt.service_name}</div>
                <div className="text-[11px] text-neutral-500">{testimonialApt.user_name} · {testimonialApt.user_visible_id}</div>
              </div>
              <button
                data-testid="testimonial-modal-close"
                onClick={() => setTestimonialApt(null)}
                className="w-8 h-8 rounded-full border border-white/10 text-neutral-300 hover:bg-white/5 flex items-center justify-center"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {testimonialBusy && !testimonialInfo ? (
              <div className="flex items-center gap-2 py-8 justify-center text-neutral-400"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
            ) : (
              <>
                {!testimonialInfo?.has_link ? (
                  <div className="text-center py-6 space-y-3">
                    <MessageSquareQuote className="w-10 h-10 text-[#d4af37]/70 mx-auto" />
                    <div className="text-neutral-300 text-sm">
                      Genera un enlace único para que el cliente publique su testimonio.
                      El enlace se abre sin necesidad de iniciar sesión y el testimonio aparecerá inmediatamente en /testimonios.
                    </div>
                    <button
                      data-testid="testimonial-generate"
                      disabled={testimonialBusy}
                      onClick={generateTestimonialLink}
                      className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#d4af37] text-black text-xs font-bold uppercase tracking-widest hover:brightness-110 disabled:opacity-50"
                    >
                      {testimonialBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                      Generar enlace de testimonio
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="p-3 rounded-lg bg-black/50 border border-white/10 text-xs">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-400 mb-1">URL única</div>
                      <div className="font-mono text-[#d4af37] break-all" data-testid="testimonial-url">{testimonialInfo.url}</div>
                      <div className="mt-1.5 flex items-center gap-3 text-[10px] text-neutral-500">
                        <span>Estado: <span className={testimonialInfo.used ? "text-emerald-400" : "text-amber-400"}>{testimonialInfo.used ? "Usado" : "Pendiente"}</span></span>
                        {testimonialInfo.expires_at && <span>Expira: {new Date(testimonialInfo.expires_at).toLocaleDateString()}</span>}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        data-testid="testimonial-copy"
                        onClick={copyTestimonialLink}
                        className="text-[11px] px-3 py-1.5 rounded border border-white/10 text-neutral-300 hover:bg-white/5 flex items-center gap-1"
                      >
                        <Fingerprint className="w-3 h-3" /> Copiar
                      </button>
                      <button
                        data-testid="testimonial-whatsapp"
                        onClick={sendTestimonialLinkWhatsApp}
                        className="text-[11px] px-3 py-1.5 rounded border border-emerald-500/50 text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 flex items-center gap-1 font-semibold"
                      >
                        <MessageCircle className="w-3 h-3" /> Enviar por WhatsApp
                      </button>
                      <a
                        data-testid="testimonial-open"
                        href={testimonialInfo.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[11px] px-3 py-1.5 rounded border border-sky-500/50 text-sky-300 bg-sky-500/10 hover:bg-sky-500/20 flex items-center gap-1"
                      >
                        <ArrowRight className="w-3 h-3" /> Abrir
                      </a>
                    </div>

                    {/* If the client already left a testimonial, show it + delete button */}
                    {testimonialInfo.comment && (
                      <div className="mt-3 p-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5">
                        <div className="text-[10px] uppercase tracking-widest text-emerald-300 mb-1 flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> Testimonio publicado
                        </div>
                        <div className="flex items-center gap-2 mb-2">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star key={i} className={`w-3 h-3 ${i < (testimonialInfo.comment.rating || 5) ? "text-[#d4af37] fill-[#d4af37]" : "text-neutral-700"}`} />
                          ))}
                          <span className="text-[10px] text-neutral-500 ml-1">
                            {testimonialInfo.comment.created_at ? new Date(testimonialInfo.comment.created_at).toLocaleDateString() : ""}
                          </span>
                        </div>
                        <p className="text-sm text-neutral-200 whitespace-pre-line">{testimonialInfo.comment.text}</p>
                        <button
                          data-testid="testimonial-delete"
                          onClick={deleteAppointmentTestimonial}
                          disabled={testimonialBusy}
                          className="mt-3 text-[11px] px-3 py-1.5 rounded border border-red-500/40 text-red-300 hover:bg-red-500/10 flex items-center gap-1 disabled:opacity-50"
                        >
                          <Trash2 className="w-3 h-3" /> Eliminar testimonio
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Receipts Gallery Modal (thumbnails grid + click-to-zoom preview) */}
      {galleryPlan && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm p-3" onClick={closeReceiptGallery}>
          <div
            data-testid="receipts-gallery-modal"
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-5xl max-h-[90vh] rounded-2xl bg-neutral-950 border border-white/10 shadow-2xl overflow-hidden flex flex-col"
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-neutral-400">Comprobantes</div>
                <div className="text-white font-semibold text-sm truncate">{galleryPlan.service_name}</div>
                <div className="text-[11px] text-neutral-500 truncate">{galleryPlan.user_name} · {galleryPlan.user_visible_id}</div>
              </div>
              <div className="flex items-center gap-2">
                {/* Filter by installment */}
                {(galleryPlan.installments && galleryPlan.installments.length > 0) && (
                  <select
                    data-testid="gallery-filter-installment"
                    value={galleryFilterInst}
                    onChange={(e) => setGalleryFilterInst(e.target.value)}
                    className="text-[11px] bg-black/60 border border-white/10 text-neutral-200 rounded px-2 py-1"
                    title="Filtrar por abono"
                  >
                    <option value="all">Todos los abonos</option>
                    <option value="unlinked">Sin vincular</option>
                    {galleryPlan.installments.map((inst, idx) => {
                      const dt = inst.at ? new Date(inst.at).toLocaleDateString("es-MX", { day: "2-digit", month: "short" }) : "";
                      return (
                        <option key={inst.id} value={inst.id}>
                          #{idx + 1} — ${Number(inst.amount || 0).toLocaleString("es-MX")} {dt && `(${dt})`}
                        </option>
                      );
                    })}
                  </select>
                )}
                {/* Export PDF */}
                <button
                  data-testid="gallery-export-pdf"
                  onClick={() => downloadPlanReportPdf(galleryPlan)}
                  className="text-[11px] px-2.5 py-1 rounded border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 flex items-center gap-1"
                  title="Descargar reporte PDF con abonos + comprobantes"
                >
                  <FileText className="w-3 h-3" /> Reporte PDF
                </button>
                <button
                  data-testid="gallery-close"
                  onClick={closeReceiptGallery}
                  className="w-8 h-8 rounded-full border border-white/10 text-neutral-300 hover:bg-white/5 flex items-center justify-center"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {galleryLoading ? (
                <div className="flex items-center justify-center py-16 text-neutral-500 gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Cargando comprobantes…
                </div>
              ) : (() => {
                const filtered = galleryItems.filter((r) => {
                  if (galleryFilterInst === "all") return true;
                  if (galleryFilterInst === "unlinked") return !r.installment_id;
                  return r.installment_id === galleryFilterInst;
                });
                if (filtered.length === 0) {
                  return (
                    <div className="text-center py-16 text-neutral-500 text-sm">
                      <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-40" />
                      {galleryFilterInst === "all"
                        ? "Aún no hay comprobantes registrados en este plan."
                        : "No hay comprobantes con este filtro."}
                    </div>
                  );
                }
                return (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="gallery-grid">
                    {filtered.map((r) => {
                      const thumb = galleryThumbs[r.id];
                      const dt = r.uploaded_at ? new Date(r.uploaded_at).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }) : "";
                      return (
                        <button
                          key={r.id}
                          data-testid={`gallery-thumb-${r.id}`}
                          onClick={() => openZoom(r)}
                          onMouseEnter={() => loadThumb(r)}
                          className="group relative rounded-xl border border-white/10 bg-black/40 overflow-hidden hover:border-[#d4af37]/60 transition"
                        >
                          <div className="aspect-square bg-neutral-900 flex items-center justify-center overflow-hidden">
                            {r.is_image ? (
                              thumb ? (
                                <img src={thumb} alt={r.filename} className="w-full h-full object-cover group-hover:scale-105 transition" />
                              ) : (
                                <div className="text-neutral-500 text-[10px] flex flex-col items-center gap-1">
                                  <ImageIcon className="w-6 h-6" />
                                  <span>Cargar</span>
                                </div>
                              )
                            ) : r.is_pdf ? (
                              <div className="text-rose-300 flex flex-col items-center gap-1">
                                <FileText className="w-8 h-8" />
                                <span className="text-[10px]">PDF</span>
                              </div>
                            ) : (
                              <div className="text-neutral-500 flex flex-col items-center gap-1">
                                <FileText className="w-8 h-8" />
                                <span className="text-[10px]">Archivo</span>
                              </div>
                            )}
                          </div>
                          <div className="px-2 py-1.5 text-left">
                            <div className="text-[10px] text-white truncate">{r.filename}</div>
                            <div className="flex items-center justify-between mt-0.5">
                              <div className="text-[9px] text-neutral-500">{dt}</div>
                              <div className={`text-[9px] px-1.5 rounded-full ${r.status === "confirmed" ? "bg-emerald-500/20 text-emerald-300" : r.status === "rejected" ? "bg-rose-500/20 text-rose-300" : "bg-amber-500/20 text-amber-300"}`}>
                                {r.status || "pending"}
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Zoom overlay */}
          {(galleryZoom || galleryZoomLoading) && (
            <div
              className="absolute inset-0 z-[75] flex items-center justify-center bg-black/95 p-4"
              onClick={(e) => { e.stopPropagation(); setGalleryZoom(null); }}
            >
              {galleryZoomLoading ? (
                <div className="flex items-center gap-2 text-neutral-300"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
              ) : galleryZoom && (
                <div className="relative max-w-4xl w-full max-h-[95vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
                  <div className="w-full flex items-center justify-between mb-2 gap-3 flex-wrap">
                    <div className="text-neutral-300 text-xs flex-1 min-w-0">
                      <div className="text-white font-medium truncate">{galleryZoom.filename}</div>
                      {galleryZoom.installment_id && (
                        <div className="text-[10px] text-sky-300">📎 Vinculado a un abono específico</div>
                      )}
                      {galleryZoom.note && (
                        <div className="text-[10px] text-neutral-400 italic mt-0.5 truncate">&ldquo;{galleryZoom.note}&rdquo;</div>
                      )}
                      <div className={`inline-block text-[10px] px-2 mt-1 rounded-full ${galleryZoom.status === "confirmed" ? "bg-emerald-500/20 text-emerald-300" : galleryZoom.status === "rejected" ? "bg-rose-500/20 text-rose-300" : "bg-amber-500/20 text-amber-300"}`}>
                        Status: {galleryZoom.status || "pending"}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {galleryZoom.status !== "confirmed" && (
                        <button
                          data-testid="gallery-approve"
                          disabled={galleryStatusBusy}
                          onClick={() => setZoomReceiptStatus("confirmed")}
                          className="text-[11px] px-2.5 py-1 rounded border border-emerald-500/50 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 flex items-center gap-1 font-semibold disabled:opacity-50"
                        >
                          <CheckCircle2 className="w-3 h-3" /> Confirmar pago
                        </button>
                      )}
                      {galleryZoom.status !== "rejected" && (
                        <button
                          data-testid="gallery-reject"
                          disabled={galleryStatusBusy}
                          onClick={() => setZoomReceiptStatus("rejected")}
                          className="text-[11px] px-2.5 py-1 rounded border border-rose-500/50 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20 flex items-center gap-1 font-semibold disabled:opacity-50"
                        >
                          <XCircle className="w-3 h-3" /> Rechazar
                        </button>
                      )}
                      <button
                        data-testid="gallery-zoom-close"
                        onClick={() => setGalleryZoom(null)}
                        className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  {galleryZoom.mime_type?.startsWith("image/") ? (
                    <img
                      data-testid="gallery-zoom-image"
                      src={`data:${galleryZoom.mime_type};base64,${galleryZoom.data_b64}`}
                      alt={galleryZoom.filename}
                      className="max-w-full max-h-[75vh] object-contain rounded-lg border border-white/10"
                    />
                  ) : (
                    <iframe
                      data-testid="gallery-zoom-pdf"
                      title={galleryZoom.filename}
                      src={`data:${galleryZoom.mime_type};base64,${galleryZoom.data_b64}`}
                      className="w-full h-[75vh] rounded-lg border border-white/10 bg-white"
                    />
                  )}
                  <a
                    data-testid="gallery-zoom-download"
                    href={`data:${galleryZoom.mime_type};base64,${galleryZoom.data_b64}`}
                    download={galleryZoom.filename}
                    className="mt-3 text-[11px] px-3 py-1.5 rounded border border-emerald-500/50 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 flex items-center gap-1"
                  >
                    <FileCheck className="w-3 h-3" /> Descargar
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
